import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { getActiveLocale, localizeMonthlyChartData } from '../../i18n/localeUtils';
import { signOut, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth, ROLES } from '../../hooks/useAuth';
import { ROUTES, APP_NAME, APP_VERSION, FIREBASE_CONFIG, CURRENCY_SYMBOL } from '../../constants';
import { formatPrice, isLocalCurrency, resolveOrderTotalCost, subscribeExchangeRates } from '../../utils';
import { calculateCustomerGrandTotal, calculateCustomerRemainingAmount } from '../../utils/customerFinancialUtils';
import { buildDeliveryReminders, hasDeliveryReminders } from '../../utils/deliveryReminderUtils';
import { groupCommissionsByPeriod, buildMonthlyCommissionTrend } from '../../utils/commissionUtils';
import {
  aggregateExpenses,
  applyExpenseDeductionsToRevenueStats,
  subtractExpensesFromMonthlyNetProfit
} from '../../utils/expenseUtils';
import {
  aggregateWorkshopExpensesFromOrders,
  aggregateWorkshopPayments,
  mergeExpenseAggregates
} from '../../utils/workshopUtils';
import { exportData, importData, validateBackupFile } from '../../utils/backupUtils';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { useLocalizedConstants } from '../../i18n/useLocalizedConstants';
import CustomerList from '../customers/CustomerList';
import DealerList from '../dealers/DealerList';
import ProductList from '../productList/ProductList';
import ExpenseList from '../expenses/ExpenseList';
// import AccountingReport from '../accounting/AccountingReport'; // GEÇİCİ: Hesaplamalar sekmesi devre dışı

import './Dashboard.css';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const EMPTY_DELIVERY_REMINDERS = {
  overdue: [],
  today: [],
  tomorrow: [],
  thisWeek: []
};

const DeliveryReminderGrid = ({ reminders, showDealerMeta = false, t }) => (
  <div className="delivery-reminders-grid">
    {reminders.overdue.length > 0 && (
      <div className="reminder-card reminder-overdue">
        <div className="reminder-header">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{t('dashboard.delivery.overdue')}</span>
          <span className="reminder-count">{reminders.overdue.length}</span>
        </div>
        <div className="reminder-list">
          {reminders.overdue.slice(0, 5).map(item => (
            <div key={item.id} className="reminder-item">
              <div className="reminder-customer-name">{item.name}</div>
              {showDealerMeta && item.dealerName && (
                <div className="reminder-dealer-name">{item.dealerName}</div>
              )}
              <div className="reminder-details">
                <span className="reminder-days">{t('common.daysOverdue', { count: item.daysOverdue })}</span>
                {item.phone && <span className="reminder-phone"><i className="fas fa-phone"></i> {item.phone}</span>}
              </div>
            </div>
          ))}
          {reminders.overdue.length > 5 && (
            <div className="reminder-more">+{reminders.overdue.length - 5} {t('common.more')}</div>
          )}
        </div>
      </div>
    )}

    {reminders.today.length > 0 && (
      <div className="reminder-card reminder-today">
        <div className="reminder-header">
          <i className="fas fa-clock"></i>
          <span>{t('dashboard.delivery.today')}</span>
          <span className="reminder-count">{reminders.today.length}</span>
        </div>
        <div className="reminder-list">
          {reminders.today.slice(0, 5).map(item => (
            <div key={item.id} className="reminder-item">
              <div className="reminder-customer-name">{item.name}</div>
              {showDealerMeta && item.dealerName && (
                <div className="reminder-dealer-name">{item.dealerName}</div>
              )}
              <div className="reminder-details">
                {item.phone && <span className="reminder-phone"><i className="fas fa-phone"></i> {item.phone}</span>}
                {item.address && <span className="reminder-address"><i className="fas fa-map-marker-alt"></i> {item.address.substring(0, 30)}...</span>}
              </div>
            </div>
          ))}
          {reminders.today.length > 5 && (
            <div className="reminder-more">+{reminders.today.length - 5} {t('common.more')}</div>
          )}
        </div>
      </div>
    )}

    {reminders.tomorrow.length > 0 && (
      <div className="reminder-card reminder-tomorrow">
        <div className="reminder-header">
          <i className="fas fa-calendar-day"></i>
          <span>{t('dashboard.delivery.tomorrow')}</span>
          <span className="reminder-count">{reminders.tomorrow.length}</span>
        </div>
        <div className="reminder-list">
          {reminders.tomorrow.slice(0, 5).map(item => (
            <div key={item.id} className="reminder-item">
              <div className="reminder-customer-name">{item.name}</div>
              {showDealerMeta && item.dealerName && (
                <div className="reminder-dealer-name">{item.dealerName}</div>
              )}
              <div className="reminder-details">
                {item.phone && <span className="reminder-phone"><i className="fas fa-phone"></i> {item.phone}</span>}
              </div>
            </div>
          ))}
          {reminders.tomorrow.length > 5 && (
            <div className="reminder-more">+{reminders.tomorrow.length - 5} {t('common.more')}</div>
          )}
        </div>
      </div>
    )}

    {reminders.thisWeek.length > 0 && (
      <div className="reminder-card reminder-week">
        <div className="reminder-header">
          <i className="fas fa-calendar-week"></i>
          <span>{t('dashboard.delivery.thisWeek')}</span>
          <span className="reminder-count">{reminders.thisWeek.length}</span>
        </div>
        <div className="reminder-list">
          {reminders.thisWeek.slice(0, 5).map(item => (
            <div key={item.id} className="reminder-item">
              <div className="reminder-customer-name">{item.name}</div>
              {showDealerMeta && item.dealerName && (
                <div className="reminder-dealer-name">{item.dealerName}</div>
              )}
              <div className="reminder-details">
                <span className="reminder-days">{t('common.daysLeft', { count: item.daysLeft })}</span>
                {item.phone && <span className="reminder-phone"><i className="fas fa-phone"></i> {item.phone}</span>}
              </div>
            </div>
          ))}
          {reminders.thisWeek.length > 5 && (
            <div className="reminder-more">+{reminders.thisWeek.length - 5} {t('common.more')}</div>
          )}
        </div>
      </div>
    )}
  </div>
);

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  const statsLocale = getActiveLocale(i18n.language);
  const { getRoleName } = useLocalizedConstants();
  const navigate = useNavigate();
  const { user, userRole, permissions, isAdmin, isMontajci, isYazilimci, loading } = useAuth();

  // LocalStorage'dan son sekmeyi oku
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('activeTab');
    return savedTab || 'dashboard';
  });

  // Rol değiştiğinde veya yüklendiğinde erişim kontrolü
  useEffect(() => {
    if (!permissions) return;

    const fallbackTab = permissions.canAccessCustomers
      ? 'customers'
      : permissions.canAccessProducts
        ? 'products'
        : permissions.canAccessDashboard
          ? 'dashboard'
          : 'customers';

    if (!permissions.canAccessDashboard && activeTab === 'dashboard') {
      setActiveTab(fallbackTab);
    }
    if (!permissions.canAccessDealers && activeTab === 'dealers') {
      setActiveTab(fallbackTab);
    }
    if (!permissions.canAccessProducts && activeTab === 'products') {
      setActiveTab(fallbackTab);
    }
    if (isMontajci && activeTab !== 'customers') {
      setActiveTab('customers');
    }
    if (!permissions.canManageUsers && activeTab === 'users') {
      setActiveTab(fallbackTab);
    }
    if (!permissions.canAccessExpenses && activeTab === 'expenses') {
      setActiveTab(fallbackTab);
    }
  }, [permissions, activeTab, isMontajci]);

  // Sekme kontrolü: Perakende veya Bayi
  const [statsTab, setStatsTab] = useState('retail');

  // Caching ve güncelleme bilgisi
  const [statsLastUpdated, setStatsLastUpdated] = useState(null);

  // Perakende İstatistikleri
  const [customerStats, setCustomerStats] = useState({
    total: 0,
    active: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0
  });
  const [revenueStats, setRevenueStats] = useState({
    expectedTotal: 0, // Tüm siparişlerin toplamı (beklenti)
    total: 0,
    totalNetProfit: 0,
    remainingTotal: 0, // Kalan ödemeler toplamı
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0,
    potentialNetProfit: 0,
    todayProfit: 0,
    thisWeekProfit: 0,
    thisMonthProfit: 0,
    thisYearProfit: 0,
    // Ödeme tipine göre günlük gelirler
    todayCash: 0, // Bugün Elden Nakit
    todayIban: 0, // Bugün IBAN
    yesterdayCreditCard: 0 // Dün Kredi Kartı (bir gün gecikme)
  });
  const [lastYearStats, setLastYearStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  });
  const [monthlyOrderTrend, setMonthlyOrderTrend] = useState([]);
  const [monthlyCustomerTrend, setMonthlyCustomerTrend] = useState([]);
  const [monthlyEarningsTrend, setMonthlyEarningsTrend] = useState([]);

  // Bayi İstatistikleri
  const [dealerStats, setDealerStats] = useState({
    total: 0,
    active: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0
  });
  const [dealerRevenueStats, setDealerRevenueStats] = useState({
    totalAmount: 0,
    paidAmount: 0,
    totalNetProfit: 0,
    remainingAmount: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0,
    potentialNetProfit: 0,
    todayProfit: 0,
    thisWeekProfit: 0,
    thisMonthProfit: 0,
    thisYearProfit: 0,
    supplierTotalDebt: 0,
    supplierTotalPurchases: 0,
    supplierTotalPaid: 0
  });
  const [dealerMonthlyOrderTrend, setDealerMonthlyOrderTrend] = useState([]);       // Sipariş listesi (session) açılış trendi
  const [dealerMonthlyOrderItemTrend, setDealerMonthlyOrderItemTrend] = useState([]); // Gerçek sipariş kalemi trendi
  const [dealerMonthlyEarningsTrend, setDealerMonthlyEarningsTrend] = useState([]);
  const [monthlyNetProfitTrend, setMonthlyNetProfitTrend] = useState([]);
  const [dealerMonthlyNetProfitTrend, setDealerMonthlyNetProfitTrend] = useState([]);
  const [deliveryReminders, setDeliveryReminders] = useState(EMPTY_DELIVERY_REMINDERS);
  const [dealerDeliveryReminders, setDealerDeliveryReminders] = useState(EMPTY_DELIVERY_REMINDERS);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const localizedMonthlyCustomerTrend = useMemo(
    () => localizeMonthlyChartData(monthlyCustomerTrend, i18n.language),
    [monthlyCustomerTrend, i18n.language]
  );
  const localizedMonthlyOrderTrend = useMemo(
    () => localizeMonthlyChartData(monthlyOrderTrend, i18n.language),
    [monthlyOrderTrend, i18n.language]
  );
  const localizedMonthlyEarningsTrend = useMemo(
    () => localizeMonthlyChartData(monthlyEarningsTrend, i18n.language),
    [monthlyEarningsTrend, i18n.language]
  );
  const localizedMonthlyNetProfitTrend = useMemo(
    () => localizeMonthlyChartData(monthlyNetProfitTrend, i18n.language),
    [monthlyNetProfitTrend, i18n.language]
  );
  const localizedDealerMonthlyOrderTrend = useMemo(
    () => localizeMonthlyChartData(dealerMonthlyOrderTrend, i18n.language),
    [dealerMonthlyOrderTrend, i18n.language]
  );
  const localizedDealerMonthlyOrderItemTrend = useMemo(
    () => localizeMonthlyChartData(dealerMonthlyOrderItemTrend, i18n.language),
    [dealerMonthlyOrderItemTrend, i18n.language]
  );
  const localizedDealerMonthlyEarningsTrend = useMemo(
    () => localizeMonthlyChartData(dealerMonthlyEarningsTrend, i18n.language),
    [dealerMonthlyEarningsTrend, i18n.language]
  );
  const localizedDealerMonthlyNetProfitTrend = useMemo(
    () => localizeMonthlyChartData(dealerMonthlyNetProfitTrend, i18n.language),
    [dealerMonthlyNetProfitTrend, i18n.language]
  );

  // Döviz kurları — tedarikçi borçlarını tengeye çevirmek için (ref: useCallback stale closure'dan kaçınmak için)
  const supplierExchangeRatesRef = React.useRef({ USD: 1, EUR: 1 });
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);

  //Firma bilgileri state'leri
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    phone: ''
  });
  const [companyError, setCompanyError] = useState(null);
  const [companySuccess, setCompanySuccess] = useState(false);
  const [deleteCompanyModal, setDeleteCompanyModal] = useState(null); // Silinecek firma bilgisi

  //Şifre değiştirme state'leri
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordConfirmModal, setShowPasswordConfirmModal] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Kullanıcı Yönetimi State'leri
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    password: '',
    displayName: '',
    role: ROLES.SATISCI
  });
  const [userFormError, setUserFormError] = useState(null);
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [deleteUserModal, setDeleteUserModal] = useState(null);
  const [commissionYear, setCommissionYear] = useState(new Date().getFullYear());

  // Aylık toplam prim trendi (tüm satışçılar) — users state'inden sonra tanımlanmalı
  const localizedCommissionTrend = useMemo(() => {
    const salesUsersList = users.filter(u => u.role === ROLES.SATISCI);
    if (salesUsersList.length === 0) return [];
    const combined = salesUsersList.flatMap(u => Array.isArray(u.commissionHistory) ? u.commissionHistory : []);
    const trend = buildMonthlyCommissionTrend(combined, currentYear);
    return localizeMonthlyChartData(
      trend.map((item) => ({ commission: item.total })),
      i18n.language
    );
  }, [users, currentYear, i18n.language]);

  // Notification Modal State
  const [notificationModal, setNotificationModal] = useState({
    show: false,
    type: 'success', // 'success', 'error', 'warning', 'info'
    title: '',
    message: '',
    details: null
  });

  // Notification Modal göster
  const showNotificationModal = useCallback((type, title, message, details = null) => {
    setNotificationModal({
      show: true,
      type,
      title,
      message,
      details
    });
  }, []);

  // Notification Modal kapat
  const closeNotificationModal = useCallback(() => {
    setNotificationModal({
      show: false,
      type: 'success',
      title: '',
      message: '',
      details: null
    });
  }, []);

  // Keep Alive Tab States (Lazy Mounting)
  const [hasMountedCustomers, setHasMountedCustomers] = useState(false);
  const [hasMountedDealers, setHasMountedDealers] = useState(false);
  const [hasMountedProducts, setHasMountedProducts] = useState(false);
  const [hasMountedExpenses, setHasMountedExpenses] = useState(false);
  const [hasMountedUsers, setHasMountedUsers] = useState(false);

  useEffect(() => {
    if (activeTab === 'customers') setHasMountedCustomers(true);
    else if (activeTab === 'dealers') setHasMountedDealers(true);
    else if (activeTab === 'products') setHasMountedProducts(true);
    else if (activeTab === 'expenses') setHasMountedExpenses(true);
    else if (activeTab === 'users') setHasMountedUsers(true);
  }, [activeTab]);

  // Döviz kurlarını çek ve saatte bir yenile
  useEffect(() => {
    return subscribeExchangeRates((rates) => {
      supplierExchangeRatesRef.current = rates;
    });
  }, []);

  // Tüm istatistikleri çek
  const fetchAllStats = useCallback(async (forceRefresh = false) => {
    try {
      setIsLoadingStats(true);

      // 30 dakikalık cache kontrolü (Firebase Read kotasını korumak için)
      if (!forceRefresh) {
        const cachedData = localStorage.getItem('dashboard_stats_cache');
        const cachedTime = localStorage.getItem('dashboard_stats_time');
        
        if (cachedData && cachedTime) {
          const ageInMs = Date.now() - parseInt(cachedTime, 10);
          const cacheLimit = 30 * 60 * 1000; // 30 Dakika
          if (ageInMs < cacheLimit) {
            const parsed = JSON.parse(cachedData);
            const cacheHasExpenseAdjustments = parsed.revenueStats?.totalExpenses !== undefined;
            if (
              parsed.dealerRevStatsData &&
              parsed.dealerRevStatsData.supplierTotalDebt !== undefined &&
              cacheHasExpenseAdjustments
            ) {
              setCustomerStats(parsed.customerStats);
              setRevenueStats(parsed.revenueStats);
              setLastYearStats(parsed.lastYearStatsTemp);
              setMonthlyOrderTrend(parsed.monthlyTrendData);
              setMonthlyCustomerTrend(parsed.monthlyCustomerTrendData);
              setMonthlyEarningsTrend(parsed.monthlyEarningsTrendData);
              setMonthlyNetProfitTrend(parsed.monthlyNetProfitTrendData);
              setDeliveryReminders(parsed.deliveryData || EMPTY_DELIVERY_REMINDERS);
              setDealerDeliveryReminders(parsed.dealerDeliveryData || EMPTY_DELIVERY_REMINDERS);

              setDealerStats(parsed.dealerStatsData);
              setDealerRevenueStats(parsed.dealerRevStatsData);
              setDealerMonthlyOrderTrend(parsed.dealerMonthlyTrendData);
              setDealerMonthlyOrderItemTrend(parsed.dealerMonthlyOrderItemData);
              setDealerMonthlyEarningsTrend(parsed.dealerMonthlyEarningsData);
              setDealerMonthlyNetProfitTrend(parsed.dealerMonthlyNetProfitData);

              setStatsLastUpdated(new Date(parseInt(cachedTime, 10)));
              setIsLoadingStats(false);
              return;
            }
          }
        }
      }

      // Yazılımcı/Rol kısıtlaması yoksa stats/dashboard birleştirilmiş belgesini (Materialized View) kontrol et
      const isFilteredView = isYazilimci && user?.uid;
      if (!forceRefresh && !isFilteredView) {
        try {
          const statsDocRef = doc(db, 'stats', 'dashboard');
          const statsDocSnap = await getDoc(statsDocRef);
          if (statsDocSnap.exists()) {
            const data = statsDocSnap.data();
            const updatedAt = data.updatedAt || Date.now();
            const ageInMs = Date.now() - updatedAt;
            const docLimit = 4 * 60 * 60 * 1000; // 4 Saat geçerlilik süresi
            
            if (ageInMs < docLimit && data.revenueStats?.totalExpenses !== undefined) {
              setCustomerStats(data.customerStats);
              setRevenueStats(data.revenueStats);
              setLastYearStats(data.lastYearStatsTemp);
              setMonthlyOrderTrend(data.monthlyTrendData);
              setMonthlyCustomerTrend(data.monthlyCustomerTrendData);
              setMonthlyEarningsTrend(data.monthlyEarningsTrendData);
              setMonthlyNetProfitTrend(data.monthlyNetProfitTrendData);
              setDeliveryReminders(data.deliveryData || EMPTY_DELIVERY_REMINDERS);
              setDealerDeliveryReminders(data.dealerDeliveryData || EMPTY_DELIVERY_REMINDERS);

              setDealerStats(data.dealerStatsData);
              setDealerRevenueStats(data.dealerRevStatsData);
              setDealerMonthlyOrderTrend(data.dealerMonthlyTrendData);
              setDealerMonthlyOrderItemTrend(data.dealerMonthlyOrderItemData);
              setDealerMonthlyEarningsTrend(data.dealerMonthlyEarningsData);
              setDealerMonthlyNetProfitTrend(data.dealerMonthlyNetProfitData);

              setStatsLastUpdated(new Date(updatedAt));

              // Local cache'i de bu veriyle doldur ki sekme geçişlerinde sıfır veritabanı okuması yapılsın
              const dataToCache = {
                customerStats: data.customerStats,
                revenueStats: data.revenueStats,
                lastYearStatsTemp: data.lastYearStatsTemp,
                monthlyTrendData: data.monthlyTrendData,
                monthlyCustomerTrendData: data.monthlyCustomerTrendData,
                monthlyEarningsTrendData: data.monthlyEarningsTrendData,
                monthlyNetProfitTrendData: data.monthlyNetProfitTrendData,
                deliveryData: data.deliveryData,
                dealerDeliveryData: data.dealerDeliveryData,
                dealerStatsData: data.dealerStatsData,
                dealerRevStatsData: data.dealerRevStatsData,
                dealerMonthlyTrendData: data.dealerMonthlyTrendData,
                dealerMonthlyOrderItemData: data.dealerMonthlyOrderItemData,
                dealerMonthlyEarningsData: data.dealerMonthlyEarningsData,
                dealerMonthlyNetProfitData: data.dealerMonthlyNetProfitData
              };
              localStorage.setItem('dashboard_stats_cache', JSON.stringify(dataToCache));
              localStorage.setItem('dashboard_stats_time', updatedAt.toString());

              setIsLoadingStats(false);
              return;
            }
          }
        } catch (docErr) {
          console.warn('Birleştirilmiş istatistik belgesi okunamadı, canlı hesaplamaya geçiliyor:', docErr);
        }
      }

      // Müşterileri çek
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      let customers = customersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Yazılımcı rolü için sadece kendi oluşturduğu müşterileri filtrele (ürün listesi etkilenmez)
      if (isYazilimci && user?.uid) {
        customers = customers.filter(c => c.createdBy === user.uid);
      }

      // Siparişleri çek
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      let orders = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));


      const now = new Date();
      const todayDelivery = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastYear = now.getFullYear() - 1;
      const lastYearYearStart = new Date(lastYear, 0, 1);
      const lastYearYearEnd = new Date(lastYear, 11, 31, 23, 59, 59, 999);
      const lastYearTodayStart = new Date(lastYear, now.getMonth(), now.getDate());
      const lastYearTodayEnd = new Date(lastYear, now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const lastYearWeekStart = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate() - 6);
      const lastYearWeekEnd = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const lastYearMonthStart = new Date(lastYear, now.getMonth(), 1);
      const lastYearMonthEnd = new Date(lastYear, now.getMonth() + 1, 0, 23, 59, 59, 999);
      const thisYear = new Date(now.getFullYear(), 0, 1);
      const thisYearDealer = new Date(now.getFullYear(), 0, 1);

      // Müşteri istatistikleri
      const customerStats = {
        total: customers.length,
        active: customers.filter(customer => {
          // Sipariş durumu "Yeni Sipariş" olan müşteriler
          return customer.orderStatus === 'new_order';
        }).length,
        today: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bugün (aynı gün kontrolü)
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const customerDate = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());
          return customerDate.getTime() === today.getTime();
        }).length,
        thisWeek: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Son 7 gün (bugün dahil)
          const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
          sevenDaysAgo.setHours(0, 0, 0, 0);
          return orderDate >= sevenDaysAgo;
        }).length,
        thisMonth: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bu ay sipariş veren müşteriler
          return orderDate >= thisMonth;
        }).length,
        thisYear: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bu yıl sipariş veren müşteriler (yılın ilk günü)
          const thisYearStart = new Date(now.getFullYear(), 0, 1);
          return orderDate >= thisYearStart;
        }).length
      };

      // Gelir istatistikleri
      const revenueStats = {
        expectedTotal: 0, // Tüm siparişlerin toplamı (beklenti)
        total: 0,
        totalNetProfit: 0,
        remainingTotal: 0, // Kalan ödemeler toplamı (Atölye + Firma + Montaj + Teslim Edildi)
        today: 0,
        thisWeek: 0,
        thisMonth: 0,
        thisYear: 0,
        potentialNetProfit: 0,
        todayProfit: 0,
        thisWeekProfit: 0,
        thisMonthProfit: 0,
        thisYearProfit: 0,
        // Ödeme tipine göre günlük gelirler
        todayCash: 0,
        todayIban: 0,
        yesterdayCreditCard: 0
      };

      const lastYearStatsTemp = {
        total: 0,
        today: 0,
        thisWeek: 0,
        thisMonth: 0
      };

      // Her müşteri için ödenen tutarları kontrol et (gerçek kazanç)
      customers.forEach(customer => {
        // Müşterinin ödenen tutarını al (gerçek kazanç)
        const paidAmount = parseFloat(customer.paidAmount || 0);

        // Toplam Gelir Beklentisi - Genel Toplam (Net) ile uyumlu hesap
        const customerOrders = orders.filter(order => order.customerId === customer.id);
        const customerTotalRevenue = calculateCustomerGrandTotal(customer, customerOrders);

        if (customerTotalRevenue > 0 || customerOrders.length > 0 || customer.manualTotalOverride != null) {
          revenueStats.expectedTotal += customerTotalRevenue;
        }

        // Toplam Gelir - Ödenen tutarlar (gerçek kazanç)
        revenueStats.total += paidAmount;

        // Toplam Net Gelir - Maliyeti olan ve onaylanmış siparişlere göre hesapla
        let totalCostForCustomerAll = 0;
        let hasValidCostOrderAll = false;
        const validStatusesForProfit = ['atolye', 'firma', 'montaj', 'delivered'];

        customerOrders.forEach(order => {
          if (order.totalCost !== undefined && order.totalCost !== null && order.totalCost !== '') {
            totalCostForCustomerAll += parseFloat(order.totalCost) || 0;
            hasValidCostOrderAll = true;
          }
        });

        if (hasValidCostOrderAll) {
          // Potansiyel Net Kazanç: Beklenen Gelir - Toplam Maliyet
          revenueStats.potentialNetProfit += (customerTotalRevenue - totalCostForCustomerAll);

          // Gerçek Net Kazanç: Alınan Ödeme - Toplam Maliyet (Onaylanmış siparişler için)
          if (validStatusesForProfit.includes(customer.orderStatus)) {
            revenueStats.totalNetProfit += (paidAmount - totalCostForCustomerAll);
          }
        }

        // Kalan Ödemeler - Atölye + Firma + Montaj + Teslim Edildi (canlı hesap, Beklenen Gelir ile uyumlu)
        if (['atolye', 'firma', 'montaj', 'delivered'].includes(customer.orderStatus)) {
          const remainingAmount = calculateCustomerRemainingAmount(customer, customerOrders);
          if (remainingAmount > 0) {
            revenueStats.remainingTotal += remainingAmount;
          }
        }

        // Ödeme geçmişinden ödeme tipine göre günlük gelirleri hesapla (sipariş tarihinden bağımsız)
        const paymentHistory = customer.paymentHistory || [];
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

        paymentHistory.forEach(payment => {
          // Firestore Timestamp veya ISO string olabilir
          let paymentDate;
          if (payment.date?.toDate) {
            paymentDate = payment.date.toDate();
          } else if (payment.date?.seconds) {
            paymentDate = new Date(payment.date.seconds * 1000);
          } else {
            paymentDate = new Date(payment.date);
          }
          const paymentDateOnly = new Date(paymentDate.getFullYear(), paymentDate.getMonth(), paymentDate.getDate());

          // Bugün Elden Nakit
          if (payment.type === 'cash' && paymentDateOnly.getTime() === today.getTime()) {
            revenueStats.todayCash += payment.amount;
          }

          // Bugün IBAN
          if (payment.type === 'iban' && paymentDateOnly.getTime() === today.getTime()) {
            revenueStats.todayIban += payment.amount;
          }

          // Dün Kredi Kartı (kredi kartı ödemeleri bir gün gecikmeyle yansır)
          if (payment.type === 'credit_card' && paymentDateOnly.getTime() === yesterday.getTime()) {
            revenueStats.yesterdayCreditCard += payment.amount;
          }
        });

        // Sipariş tarihi yoksa zaman bazlı istatistiklere dahil etme
        if (!customer.orderDate) return;

        // Müşterinin sipariş tarihini al
        let customerDate;
        if (customer.orderDate?.toDate) {
          customerDate = customer.orderDate.toDate();
        } else {
          customerDate = new Date(customer.orderDate);
        }

        // Bugün - Bugün sipariş veren müşterilerin ödenen tutarları
        const customerDateOnly = new Date(customerDate.getFullYear(), customerDate.getMonth(), customerDate.getDate());
        if (customerDateOnly.getTime() === today.getTime()) {
          revenueStats.today += paidAmount;
        }

        // Bu hafta - Son 7 gün içinde sipariş veren müşterilerin ödenen tutarları (bugün dahil)
        const sevenDaysAgoRevenue = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        sevenDaysAgoRevenue.setHours(0, 0, 0, 0);
        if (customerDate >= sevenDaysAgoRevenue) {
          revenueStats.thisWeek += paidAmount;
        }

        // Bu ay - Bu ay sipariş veren müşterilerin ödenen tutarları
        if (customerDate >= thisMonth) {
          revenueStats.thisMonth += paidAmount;
        }

        // Bu yıl - Bu yıl sipariş veren müşterilerin ödenen tutarları
        if (customerDate >= thisYear) {
          revenueStats.thisYear += paidAmount;
        }

        // Zaman Bazlı Kâr Hesaplama (Sadece onaylanmış/yeni sipariş olmayanlar için)
        if (hasValidCostOrderAll && validStatusesForProfit.includes(customer.orderStatus)) {
          const profitFromCustomer = paidAmount - totalCostForCustomerAll;

          if (customerDateOnly.getTime() === today.getTime()) {
            revenueStats.todayProfit += profitFromCustomer;
          }
          if (customerDate >= sevenDaysAgoRevenue) {
            revenueStats.thisWeekProfit += profitFromCustomer;
          }
          if (customerDate >= thisMonth) {
            revenueStats.thisMonthProfit += profitFromCustomer;
          }
          if (customerDate >= thisYear) {
            revenueStats.thisYearProfit += profitFromCustomer;
          }
        }

        // Geçen yıl karşılaştırmaları
        if (customerDate >= lastYearYearStart && customerDate <= lastYearYearEnd) {
          lastYearStatsTemp.total += 1;
        }

        if (customerDate >= lastYearTodayStart && customerDate <= lastYearTodayEnd) {
          lastYearStatsTemp.today += 1;
        }

        if (customerDate >= lastYearWeekStart && customerDate <= lastYearWeekEnd) {
          lastYearStatsTemp.thisWeek += 1;
        }

        if (customerDate >= lastYearMonthStart && customerDate <= lastYearMonthEnd) {
          lastYearStatsTemp.thisMonth += 1;
        }
      });

      // Aylık sipariş trendi (mevcut yıl)
      const monthLabels = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
      const currentYear = now.getFullYear();
      const monthlyTrendCounts = Array(12).fill(0);
      const monthlyCustomerCounts = Array(12).fill(0);

      // Sipariş tarihlerini müşteriden okur (orders içinde orderDate yok)
      const customerOrderDateMap = new Map();
      customers.forEach(customer => {
        const dateToUse = customer.orderDate;
        let parsed = null;
        if (dateToUse?.toDate) {
          parsed = dateToUse.toDate();
        } else if (dateToUse) {
          parsed = new Date(dateToUse);
        }
        if (parsed && !isNaN(parsed.getTime())) {
          customerOrderDateMap.set(customer.id, parsed);
        }
      });

      orders.forEach(order => {
        // Sipariş Tarihi müşteri kaydından alınır; yoksa ekleme
        const orderDate = customerOrderDateMap.get(order.customerId);
        if (!orderDate) return;
        if (orderDate.getFullYear() === currentYear) {
          let orderQuantity = 0;

          if (order.productType === 'plise') {
            // Plise siparişleri için pliseItems listesindeki adetleri topla
            if (Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
              orderQuantity = order.pliseItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0);
            } else {
              // Yedek olarak ana quantity alanını kontrol et
              orderQuantity = parseInt(order.quantity) || 1;
            }
          } else {
            // Diğer tüm ürünler için ana quantity alanını kullan
            orderQuantity = parseInt(order.quantity) || 1;
          }

          monthlyTrendCounts[orderDate.getMonth()] += orderQuantity;
        }
      });

      customers.forEach(customer => {
        // Müşteri trendi de sipariş tarihine göre; tarih yoksa ekleme
        const dateToUse = customer.orderDate;
        let customerDate = null;

        if (dateToUse?.toDate) {
          customerDate = dateToUse.toDate();
        } else if (dateToUse) {
          customerDate = new Date(dateToUse);
        }

        if (!customerDate || isNaN(customerDate.getTime())) return;

        if (customerDate.getFullYear() === currentYear) {
          monthlyCustomerCounts[customerDate.getMonth()] += 1;
        }
      });

      const monthlyTrendData = monthLabels.map((label, index) => ({
        month: label,
        count: monthlyTrendCounts[index]
      }));

      const monthlyCustomerTrendData = monthLabels.map((label, index) => ({
        month: label,
        count: monthlyCustomerCounts[index]
      }));

      // Aylık Kazanç ve Net Kazanç Trendi Hesaplama
      const monthlyEarningsCounts = Array(12).fill(0);
      const monthlyNetProfitCounts = Array(12).fill(0);

      customers.forEach(customer => {
        // Müşterinin sipariş tarihini al
        const dateToUse = customer.orderDate;
        let customerDate = null;

        if (dateToUse?.toDate) {
          customerDate = dateToUse.toDate();
        } else if (dateToUse) {
          customerDate = new Date(dateToUse);
        }

        if (!customerDate || isNaN(customerDate.getTime())) return;
        if (customerDate.getFullYear() !== currentYear) return;

        // Müşterinin ödenen tutarını al (gerçek kazanç)
        const paidAmount = parseFloat(customer.paidAmount || 0);

        // Aylık kazanca ekle (ödenen tutar)
        monthlyEarningsCounts[customerDate.getMonth()] += paidAmount;

        // Sipariş maliyetleri üzerinden Net Kazanç Hesapla
        const customerOrders = orders.filter(o => o.customerId === customer.id);
        let totalCostForCustomer = 0;
        let hasValidCostOrder = false;

        customerOrders.forEach(order => {
          if (order.totalCost !== undefined && order.totalCost !== null && order.totalCost !== '') {
            totalCostForCustomer += parseFloat(order.totalCost) || 0;
            hasValidCostOrder = true;
          }
        });

        // Sadece geçerli (maliyeti olan yani yeni sistem) ve durumu onaylanmış (Yeni Sipariş olmayan) siparişleri kar hesaplamasına dahil et
        const validStatusesForProfitGraph = ['atolye', 'firma', 'montaj', 'delivered'];
        if (hasValidCostOrder && validStatusesForProfitGraph.includes(customer.orderStatus)) {
          const netProfit = paidAmount - totalCostForCustomer;
          monthlyNetProfitCounts[customerDate.getMonth()] += netProfit;
        }
      });

      const monthlyEarningsTrendData = monthLabels.map((label, index) => ({
        month: label,
        earnings: monthlyEarningsCounts[index]
      }));
      const monthlyNetProfitTrendData = monthLabels.map((label, index) => ({
        month: label,
        netProfit: monthlyNetProfitCounts[index]
      }));

      // Teslimat Hatırlatıcı — Perakende
      const retailDeliveryEntries = customers
        .filter(c => c.orderStatus && c.orderStatus !== 'delivered' && c.orderStatus !== 'cancelled' && c.deliveryDate)
        .map(customer => ({
          id: customer.id,
          type: 'retail',
          name: customer.name,
          phone: customer.phone1,
          address: customer.address,
          deliveryDate: customer.deliveryDate,
          orderStatus: customer.orderStatus
        }));
      const deliveryData = buildDeliveryReminders(retailDeliveryEntries);

      // ==========================================
      // BAYİ İSTATİSTİKLERİ (DEALER SESSIONS)
      // ==========================================
      // Sistemde hala aktif (silinmemiş) olan bayileri çek
      const dealersSnapshot = await getDocs(collection(db, 'dealers'));
      let activeDealers = dealersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const activeDealerIds = activeDealers.map(d => d.id);

      // Bayi Sipariş Listelerini çek
      const dealerSessionsSnapshot = await getDocs(collection(db, 'dealer_sessions'));
      let dealerSessions = dealerSessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // SADECE silinmemiş, aktif bayilere ait olan Sipariş Listelerini (Sessions) tut
      dealerSessions = dealerSessions.filter(s => activeDealerIds.includes(s.dealerId));

      if (isYazilimci && user?.uid) {
        dealerSessions = dealerSessions.filter(s => s.createdBy === user.uid);
      }

      // Teslimat Hatırlatıcı — Bayi Sipariş Listeleri
      const dealerById = Object.fromEntries(activeDealers.map(d => [d.id, d]));
      const dealerDeliveryEntries = dealerSessions
        .filter(s => s.deliveryDate && s.orderStatus !== 'delivered' && s.orderStatus !== 'cancelled')
        .map(session => {
          const dealer = dealerById[session.dealerId] || {};
          return {
            id: session.id,
            type: 'dealer',
            name: session.name || `${dealer.companyName || 'Bayi'} Sipariş Listesi`,
            dealerName: dealer.companyName || '',
            phone: dealer.phone1 || '',
            address: dealer.address || '',
            deliveryDate: session.deliveryDate,
            orderStatus: session.orderStatus
          };
        });
      const dealerDeliveryData = buildDeliveryReminders(dealerDeliveryEntries);

      // Tedarikçi Alım Listelerini çek
      const supplierSessionsSnapshot = await getDocs(collection(db, 'supplier_sessions'));
      let supplierSessions = supplierSessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // SADECE silinmemiş, aktif tedarikçilere ait olan Alım Listelerini (Sessions) tut
      supplierSessions = supplierSessions.filter(s => activeDealerIds.includes(s.dealerId));

      if (isYazilimci && user?.uid) {
        supplierSessions = supplierSessions.filter(s => s.createdBy === user.uid);
      }

      const dealerSessionIds = dealerSessions.map(s => s.id);
      const dealerOrders = orders.filter(o => dealerSessionIds.includes(o.customerId));

      const totalDealerProducts = dealerOrders.reduce((sum, order) => {
        if (order.productType === 'plise' && Array.isArray(order.pliseItems)) {
          return sum + order.pliseItems.reduce((pSum, item) => pSum + (parseInt(item.quantity) || 0), 0);
        }
        return sum + (parseInt(order.quantity) || 0);
      }, 0);

      const dealerStatsData = {
        total: dealerSessions.length,
        totalProducts: totalDealerProducts,
        today: 0,
        thisWeek: 0,
        thisMonth: 0,
        thisYear: 0
      };

      let supplierTotalDebtTemp = 0;
      let supplierTotalPurchasesTemp = 0;
      let supplierTotalPaidTemp = 0;

      // Anlık exchange rates (ref üzerinden erişim — stale closure'dan güvenli)
      const currentExchangeRates = supplierExchangeRatesRef.current;

      supplierSessions.forEach(session => {
        if (session.orderStatus === 'cancelled') return;

        const cur = session.currency || 'KZT';
        const rate = isLocalCurrency(cur) ? 1 : (parseFloat(currentExchangeRates[cur]) || 1);

        const totalAmt = parseFloat(session.totalOrderAmount || 0);
        const paid = parseFloat(session.paidAmount || 0);
        const remaining = parseFloat(session.remainingAmount ?? (totalAmt - paid));

        // Dashboard toplamları tenge bazında
        supplierTotalPurchasesTemp += totalAmt * rate;
        supplierTotalPaidTemp += paid * rate;
        supplierTotalDebtTemp += remaining * rate;
      });

      const dealerRevStatsData = {
        totalAmount: 0,
        paidAmount: 0,
        totalNetProfit: 0,
        remainingAmount: 0,
        today: 0,
        thisWeek: 0,
        thisMonth: 0,
        thisYear: 0,
        potentialNetProfit: 0,
        todayProfit: 0,
        thisWeekProfit: 0,
        thisMonthProfit: 0,
        thisYearProfit: 0,
        supplierTotalDebt: supplierTotalDebtTemp,
        supplierTotalPurchases: supplierTotalPurchasesTemp,
        supplierTotalPaid: supplierTotalPaidTemp
      };

      const dealerMonthlyTrendCounts = Array(12).fill(0);
      const dealerMonthlyEarningsCounts = Array(12).fill(0);
      const dealerMonthlyNetProfitCounts = Array(12).fill(0);

      // Ana Sipariş Listeleri (Sessions) üzerinden istatistik ve gelir hesaplamaları
      dealerSessions.forEach(session => {
        let sessionDate = null;
        if (session.orderDate) {
          sessionDate = new Date(session.orderDate + 'T00:00:00');
        } else if (session.sessionDate) {
          sessionDate = new Date(session.sessionDate + 'T00:00:00');
        } else if (session.createdAt?.toDate) {
          sessionDate = session.createdAt.toDate();
        } else if (session.createdAt) {
          sessionDate = new Date(session.createdAt);
        }

        const totalAmt = (session.manualTotalOverride != null && session.manualTotalOverride !== '')
          ? parseFloat(session.manualTotalOverride)
          : parseFloat(session.totalOrderAmount || 0);
        const paidAmt = parseFloat(session.paidAmount || 0);
        const remAmt = parseFloat(session.remainingAmount ?? (totalAmt - paidAmt));

        dealerRevStatsData.totalAmount += totalAmt;
        dealerRevStatsData.paidAmount += paidAmt;

        // Sipariş maliyetleri üzerinden Toplam Net Kazanç Hesapla
        const sessionOrdersAll = dealerOrders.filter(o => o.customerId === session.id);
        let totalCostForSessionAll = 0;
        let hasValidCostOrderAll = false;

        sessionOrdersAll.forEach(order => {
          totalCostForSessionAll += resolveOrderTotalCost(order);
        });
        hasValidCostOrderAll = sessionOrdersAll.length > 0;

        if (hasValidCostOrderAll) {
          // Potansiyel Net Kazanç: Toplam Beklenen Tutar - Toplam Maliyet (iptal hariç, new_order dahil)
          if (session.orderStatus !== 'cancelled') {
            dealerRevStatsData.potentialNetProfit += (totalAmt - totalCostForSessionAll);
          }

          // Gerçek Net Kazanç: Alınan Ödeme - Toplam Maliyet (yeni sipariş ve iptal hariç)
          if (session.orderStatus !== 'cancelled' && session.orderStatus !== 'new_order') {
            dealerRevStatsData.totalNetProfit += (paidAmt - totalCostForSessionAll);
          }
        }

        if (session.orderStatus !== 'cancelled') {
          dealerRevStatsData.remainingAmount += remAmt;
        }

        if (!sessionDate || isNaN(sessionDate.getTime())) return;

        const dateOnly = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());

        // Stats
        if (dateOnly.getTime() === todayDelivery.getTime()) {
          dealerStatsData.today += 1;
          dealerRevStatsData.today += paidAmt;
        }

        const sevenDaysAgoDealer = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        sevenDaysAgoDealer.setHours(0, 0, 0, 0);
        if (sessionDate >= sevenDaysAgoDealer) {
          dealerStatsData.thisWeek += 1;
          dealerRevStatsData.thisWeek += paidAmt;
        }

        if (sessionDate >= thisMonth) {
          dealerStatsData.thisMonth += 1;
          dealerRevStatsData.thisMonth += paidAmt;
        }

        if (sessionDate >= thisYearDealer) {
          dealerStatsData.thisYear += 1;
          dealerRevStatsData.thisYear += paidAmt;
        }

        // Zaman Bazlı Kâr Hesaplama (yeni sipariş ve iptal edilmemiş olanlar)
        if (hasValidCostOrderAll && session.orderStatus !== 'cancelled' && session.orderStatus !== 'new_order') {
          const profitFromSession = paidAmt - totalCostForSessionAll;

          if (dateOnly.getTime() === todayDelivery.getTime()) {
            dealerRevStatsData.todayProfit += profitFromSession;
          }
          if (sessionDate >= sevenDaysAgoDealer) {
            dealerRevStatsData.thisWeekProfit += profitFromSession;
          }
          if (sessionDate >= thisMonth) {
            dealerRevStatsData.thisMonthProfit += profitFromSession;
          }
          if (sessionDate >= thisYearDealer) {
            dealerRevStatsData.thisYearProfit += profitFromSession;
          }
        }

        if (sessionDate.getFullYear() === currentYear) {
          dealerMonthlyTrendCounts[sessionDate.getMonth()] += 1;
          dealerMonthlyEarningsCounts[sessionDate.getMonth()] += paidAmt;

          // Sipariş maliyetleri üzerinden Net Kazanç Hesapla (aynı plise fallback mantığıyla)
          const sessionOrders = dealerOrders.filter(o => o.customerId === session.id);
          let totalCostForSession = 0;
          let hasValidCostOrder = false;

          sessionOrders.forEach(order => {
            totalCostForSession += resolveOrderTotalCost(order);
          });
          hasValidCostOrder = sessionOrders.length > 0;

          if (hasValidCostOrder && session.orderStatus !== 'cancelled' && session.orderStatus !== 'new_order') {
            dealerMonthlyNetProfitCounts[sessionDate.getMonth()] += (paidAmt - totalCostForSession);
          }
        }
      });

      const dealerMonthlyTrendData = monthLabels.map((label, index) => ({
        month: label,
        count: dealerMonthlyTrendCounts[index]
      }));
      const dealerMonthlyEarningsData = monthLabels.map((label, index) => ({
        month: label,
        earnings: dealerMonthlyEarningsCounts[index]
      }));
      const dealerMonthlyNetProfitData = monthLabels.map((label, index) => ({
        month: label,
        netProfit: dealerMonthlyNetProfitCounts[index]
      }));

      // Gerçek sipariş kalemleri (orders) için aylık trend — bağlı session'ın orderDate'ine göre
      // Session ID → orderDate lookup (order kalemleri için doğru ay tespiti)
      const sessionDateMap = {};
      dealerSessions.forEach(s => {
        if (s.orderDate) {
          sessionDateMap[s.id] = new Date(s.orderDate + 'T00:00:00');
        } else if (s.sessionDate) {
          sessionDateMap[s.id] = new Date(s.sessionDate + 'T00:00:00');
        } else if (s.createdAt?.toDate) {
          sessionDateMap[s.id] = s.createdAt.toDate();
        } else if (s.createdAt) {
          sessionDateMap[s.id] = new Date(s.createdAt);
        }
      });

      const dealerOrderItemMonthlyCounts = Array(12).fill(0);
      dealerOrders.forEach(order => {
        // Sipariş kaleminin tarihi olarak bağlı session'ın orderDate'ini kullan
        let orderDate = sessionDateMap[order.customerId] || null;

        // Fallback: session bulunamazsa order'ın kendi createdAt'ini kullan
        if (!orderDate) {
          if (order.createdAt?.toDate) {
            orderDate = order.createdAt.toDate();
          } else if (order.createdAt) {
            orderDate = new Date(order.createdAt);
          }
        }
        if (!orderDate || isNaN(orderDate.getTime())) return;

        if (orderDate.getFullYear() === currentYear) {
          let orderQuantity = 0;

          if (order.productType === 'plise') {
            // Plise siparişleri için pliseItems listesindeki adetleri topla
            if (Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
              orderQuantity = order.pliseItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0);
            } else {
              // Yedek olarak ana quantity alanını kontrol et
              orderQuantity = parseInt(order.quantity) || 1;
            }
          } else {
            // Diğer tüm ürünler için ana quantity alanını kullan
            orderQuantity = parseInt(order.quantity) || 1;
          }

          dealerOrderItemMonthlyCounts[orderDate.getMonth()] += orderQuantity;
        }
      });
      const dealerMonthlyOrderItemData = monthLabels.map((label, index) => ({
        month: label,
        count: dealerOrderItemMonthlyCounts[index]
      }));

      // Giderler — net kazanç hesaplamalarından düş (manuel + atölye dikim)
      const expensesSnapshot = await getDocs(collection(db, 'expenses'));
      const expenses = expensesSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      const settingsSnap = await getDoc(doc(db, 'settings', 'global'));
      const adminSettings = settingsSnap.exists() ? settingsSnap.data() : {};
      const manualExpenseAgg = aggregateExpenses(expenses, now);
      const workshopExpenseAgg = aggregateWorkshopExpensesFromOrders(orders, customers, adminSettings, now);
      const expenseAgg = mergeExpenseAggregates(manualExpenseAgg, workshopExpenseAgg);

      const adjustedRevenueStats = applyExpenseDeductionsToRevenueStats(revenueStats, expenseAgg);
      const adjustedDealerRevStats = applyExpenseDeductionsToRevenueStats(dealerRevStatsData, expenseAgg);

      const adjustedMonthlyNetProfitCounts = subtractExpensesFromMonthlyNetProfit(
        monthlyNetProfitCounts,
        expenseAgg.monthlyCounts
      );
      const adjustedMonthlyNetProfitTrendData = monthLabels.map((label, index) => ({
        month: label,
        netProfit: adjustedMonthlyNetProfitCounts[index]
      }));

      const adjustedDealerMonthlyNetProfitCounts = subtractExpensesFromMonthlyNetProfit(
        dealerMonthlyNetProfitCounts,
        expenseAgg.monthlyCounts
      );
      const adjustedDealerMonthlyNetProfitData = monthLabels.map((label, index) => ({
        month: label,
        netProfit: adjustedDealerMonthlyNetProfitCounts[index]
      }));

      // Set Retail Stats
      setCustomerStats(customerStats);
      setRevenueStats(adjustedRevenueStats);
      setLastYearStats(lastYearStatsTemp);
      setMonthlyOrderTrend(monthlyTrendData);
      setMonthlyCustomerTrend(monthlyCustomerTrendData);
      setMonthlyEarningsTrend(monthlyEarningsTrendData);
      setMonthlyNetProfitTrend(adjustedMonthlyNetProfitTrendData);
      setDeliveryReminders(deliveryData);
      setDealerDeliveryReminders(dealerDeliveryData);

      // Set Dealer Stats
      setDealerStats(dealerStatsData);
      setDealerRevenueStats(adjustedDealerRevStats);
      setDealerMonthlyOrderTrend(dealerMonthlyTrendData);
      setDealerMonthlyOrderItemTrend(dealerMonthlyOrderItemData);
      setDealerMonthlyEarningsTrend(dealerMonthlyEarningsData);
      setDealerMonthlyNetProfitTrend(adjustedDealerMonthlyNetProfitData);

      // Cache'e ve Materialized View'e yaz
      const dataToCache = {
        customerStats,
        revenueStats: adjustedRevenueStats,
        lastYearStatsTemp,
        monthlyTrendData,
        monthlyCustomerTrendData,
        monthlyEarningsTrendData,
        monthlyNetProfitTrendData: adjustedMonthlyNetProfitTrendData,
        deliveryData,
        dealerDeliveryData,
        dealerStatsData,
        dealerRevStatsData: adjustedDealerRevStats,
        dealerMonthlyTrendData,
        dealerMonthlyOrderItemData,
        dealerMonthlyEarningsData,
        dealerMonthlyNetProfitData: adjustedDealerMonthlyNetProfitData
      };
      
      const updateTime = new Date();
      localStorage.setItem('dashboard_stats_cache', JSON.stringify(dataToCache));
      localStorage.setItem('dashboard_stats_time', updateTime.getTime().toString());
      setStatsLastUpdated(updateTime);

      // Yazılımcı filtresi yoksa bu güncel veriyi tek bir write ile stats/dashboard belgesine de yaz
      if (!isFilteredView) {
        try {
          const statsDocRef = doc(db, 'stats', 'dashboard');
          await setDoc(statsDocRef, {
            ...dataToCache,
            updatedAt: updateTime.getTime()
          });
        } catch (writeErr) {
          console.error('Birleştirilmiş istatistik belgesi güncellenemedi:', writeErr);
        }
      }

    } catch (error) {
      console.error('İstatistikler yüklenirken hata:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, [isYazilimci, user?.uid]);

  const handleCustomerChange = useCallback(() => {
    localStorage.removeItem('dashboard_stats_cache');
    localStorage.removeItem('dashboard_stats_time');
    setStatsLastUpdated(null);
    fetchAllStats(true); // Canlı verileri tekrar hesapla
  }, [fetchAllStats]);

  const handleExpenseChange = useCallback(() => {
    localStorage.removeItem('dashboard_stats_cache');
    localStorage.removeItem('dashboard_stats_time');
    setStatsLastUpdated(null);
    fetchAllStats(true);
  }, [fetchAllStats]);

  // İstatistikleri yükle
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchAllStats();
    }
  }, [fetchAllStats, activeTab]);

  // Aktif sekme değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);



  // Firma bilgilerini Firebase'den çek
  const fetchCompanies = useCallback(async () => {
    try {
      setIsLoadingCompanies(true);
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      const companiesData = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // İsme göre sırala
      companiesData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCompanies(companiesData);
    } catch (error) {
      console.error('Firma bilgileri yüklenirken hata:', error);
      setCompanyError(t('dashboard.company.errors.loadFailed'));
    } finally {
      setIsLoadingCompanies(false);
    }
  }, [t]);

  // Firma bilgileri modalı açıldığında verileri çek
  useEffect(() => {
    if (showCompanyInfo) {
      fetchCompanies();
    }
  }, [showCompanyInfo, fetchCompanies]);

  // Firma ekle veya güncelle
  const handleSaveCompany = async (e) => {
    e.preventDefault();
    setCompanyError(null);
    setCompanySuccess(false);

    // Validasyonlar
    if (!companyForm.name || !companyForm.phone) {
      setCompanyError(t('dashboard.company.errors.requiredFields'));
      return;
    }

    // Telefon numarası validasyonu (sadece rakam ve + işareti)
    const phoneRegex = /^[+]?[0-9]{10,15}$/;
    if (!phoneRegex.test(companyForm.phone.replace(/\s/g, ''))) {
      setCompanyError(t('dashboard.company.errors.invalidPhone'));
      return;
    }

    try {
      const phoneNumber = companyForm.phone.replace(/\s/g, ''); // Boşlukları temizle

      if (editingCompany) {
        // Güncelleme
        await updateDoc(doc(db, 'companies', editingCompany.id), {
          name: companyForm.name.trim(),
          phone: phoneNumber,
          updatedAt: new Date()
        });
        setCompanySuccess(t('dashboard.company.success.updated'));
      } else {
        // Yeni ekleme
        // Aynı isimde firma var mı kontrol et
        const existingCompany = companies.find(c =>
          c.name.toLowerCase().trim() === companyForm.name.toLowerCase().trim()
        );
        if (existingCompany) {
          setCompanyError(t('dashboard.company.errors.duplicateName'));
          return;
        }

        await addDoc(collection(db, 'companies'), {
          name: companyForm.name.trim(),
          phone: phoneNumber,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: user?.uid || null
        });
        setCompanySuccess(t('dashboard.company.success.added'));
      }

      // Formu temizle
      setCompanyForm({ name: '', phone: '' });
      setEditingCompany(null);

      // Listeyi yeniden yükle
      await fetchCompanies();

      // 3 saniye sonra başarı mesajını kaldır
      setTimeout(() => {
        setCompanySuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Firma kaydedilirken hata:', error);
      let errorMessage = t('dashboard.company.errors.saveFailed');
      if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
        errorMessage = t('dashboard.company.errors.noPermission');
      } else if (error.message) {
        errorMessage = t('dashboard.company.errors.saveFailedWithMessage', { message: error.message });
      }
      setCompanyError(errorMessage);
    }
  };

  // Firma düzenle
  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name || '',
      phone: company.phone || ''
    });
    setCompanyError(null);
    setCompanySuccess(false);
  };

  // Firma silme modal'ını aç
  const handleDeleteCompanyClick = (company) => {
    setDeleteCompanyModal(company);
  };

  // Firma silme modal'ını kapat
  const handleCloseDeleteCompanyModal = () => {
    setDeleteCompanyModal(null);
  };

  // Firma sil
  const handleDeleteCompany = async () => {
    if (!deleteCompanyModal) return;

    try {
      await deleteDoc(doc(db, 'companies', deleteCompanyModal.id));
      setCompanySuccess(t('dashboard.company.success.deleted'));
      setDeleteCompanyModal(null);
      await fetchCompanies();

      // 3 saniye sonra başarı mesajını kaldır
      setTimeout(() => {
        setCompanySuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Firma silinirken hata:', error);
      setCompanyError(t('dashboard.company.errors.deleteFailed', { message: error.message }));
      setDeleteCompanyModal(null);
    }
  };

  // Firma formunu iptal et
  const handleCancelCompany = () => {
    setCompanyForm({ name: '', phone: '' });
    setEditingCompany(null);
    setCompanyError(null);
    setCompanySuccess(false);
  };

  // ======== KULLANICI YÖNETİMİ FONKSİYONLARI ========

  // Kullanıcıları getir
  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;

    setIsLoadingUsers(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(user => !user.disabled); // Disabled kullanıcıları filtrele
      setUsers(usersData);
    } catch (error) {
      console.error('Kullanıcılar alınırken hata:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [isAdmin]);

  // Kullanıcıları yükle (admin ise)
  useEffect(() => {
    if (isAdmin && activeTab === 'users') {
      fetchUsers();
    }
  }, [isAdmin, activeTab, fetchUsers]);

  // Yeni kullanıcı oluştur
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserFormError(null);

    if (!newUserForm.email || !newUserForm.password || !newUserForm.displayName) {
      setUserFormError(t('users.createForm.errors.fillAllFields'));
      return;
    }

    if (newUserForm.password.length < 6) {
      setUserFormError(t('users.createForm.errors.passwordMinLength'));
      return;
    }

    // Yazılımcı rolü kısıtlamaları
    if (newUserForm.role === ROLES.YAZILIMCI) {
      if (newUserForm.email !== 'kahramantechinfo@gmail.com') {
        setUserFormError(t('users.createForm.errors.yazilimciEmailOnly'));
        return;
      }
      const mevcutYazilimci = users.find(u => u.role === ROLES.YAZILIMCI);
      if (mevcutYazilimci) {
        setUserFormError(t('users.createForm.errors.yazilimciAlreadyExists'));
        return;
      }
    }

    try {
      // Firebase Auth'da kullanıcı oluştur - Secondary auth instance kullanıyoruz
      // Ana kullanıcının oturumunu kapatmadan yeni kullanıcı oluşturmak için
      const { initializeApp, getApps, deleteApp } = await import('firebase/app');
      const { getAuth, createUserWithEmailAndPassword: createUser } = await import('firebase/auth');

      // Mevcut Secondary app varsa sil
      const apps = getApps();
      const existingSecondary = apps.find(app => app.name === 'Secondary');
      if (existingSecondary) {
        await deleteApp(existingSecondary);
      }

      // Geçici bir Firebase app instance oluştur (constants'dan config al)
      const secondaryApp = initializeApp(FIREBASE_CONFIG, 'Secondary');
      const secondaryAuth = getAuth(secondaryApp);

      try {
        const userCredential = await createUser(secondaryAuth, newUserForm.email, newUserForm.password);
        const newUser = userCredential.user;

        // Firestore'da kullanıcı kaydı oluştur
        await setDoc(doc(db, 'users', newUser.uid), {
          email: newUserForm.email,
          displayName: newUserForm.displayName,
          role: newUserForm.role,
          createdAt: new Date(),
          createdBy: user.uid
        });

        // Secondary app'i temizle
        await deleteApp(secondaryApp);

        // Formu sıfırla ve modalı kapat
        const createdDisplayName = newUserForm.displayName;
        setNewUserForm({
          email: '',
          password: '',
          displayName: '',
          role: ROLES.SATISCI
        });
        setShowAddUserModal(false);

        // Kullanıcı listesini yenile
        fetchUsers();

        showNotificationModal(
          'success',
          t('common.success'),
          t('users.createForm.success', { name: createdDisplayName })
        );
      } catch (error) {
        await deleteApp(secondaryApp);
        throw error;
      }
    } catch (error) {
      console.error('Kullanıcı oluşturulurken hata:', error);
      if (error.code === 'auth/email-already-in-use') {
        setUserFormError(t('users.createForm.errors.emailInUse'));
      } else if (error.code === 'auth/invalid-email') {
        setUserFormError(t('users.createForm.errors.invalidEmail'));
      } else if (error.code === 'auth/weak-password') {
        setUserFormError(t('users.createForm.errors.weakPassword'));
      } else {
        setUserFormError(t('users.createForm.errors.createFailed'));
      }
    }
  };

  // Kullanıcı rolünü güncelle
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: new Date(),
        updatedBy: user.uid
      });

      // Local state güncelle
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, role: newRole } : u
      ));
      setEditingUserRole(null);

      showNotificationModal('success', 'Başarılı', 'Kullanıcı rolü güncellendi');
    } catch (error) {
      console.error('Rol güncellenirken hata:', error);
      showNotificationModal('error', 'Hata', 'Rol güncellenirken bir hata oluştu');
    }
  };

  // Kullanıcı sil - Kullanıcıyı disabled olarak işaretle (Firestore'dan silme, sadece devre dışı bırak)
  const handleDeleteUser = async (userId) => {
    try {
      // Önce kullanıcı bilgilerini al
      const userToDelete = users.find(u => u.id === userId);
      if (!userToDelete) {
        showNotificationModal('error', 'Hata', 'Kullanıcı bulunamadı');
        return;
      }

      // Firebase Auth'dan kullanıcıyı silmek için Cloud Functions endpoint'ini dene
      try {
        const functions = await import('firebase/functions');
        const { getFunctions, httpsCallable } = functions;
        const funcs = getFunctions();
        const deleteUserFunction = httpsCallable(funcs, 'deleteUser');

        // Cloud Functions ile Firebase Auth'dan da sil
        await deleteUserFunction({ userId: userId, email: userToDelete.email });

        // Başarılı olursa Firestore'dan da sil
        await deleteDoc(doc(db, 'users', userId));
      } catch (functionsError) {
        // Cloud Functions yoksa veya hata varsa, sadece disabled olarak işaretle
        // Firestore'dan SİLME, sadece disabled yap
        await updateDoc(doc(db, 'users', userId), {
          disabled: true,
          disabledAt: new Date(),
          disabledBy: user.uid
        });
      }

      // Kullanıcı listesini yeniden yükle
      await fetchUsers();

      setDeleteUserModal(null);
      showNotificationModal('success', 'Başarılı', 'Kullanıcı başarıyla devre dışı bırakıldı');
    } catch (error) {
      console.error('Kullanıcı silinirken hata:', error);
      showNotificationModal('error', 'Hata', 'Kullanıcı silinirken bir hata oluştu: ' + error.message);
    }
  };

  // Rol adını aktif dile göre döndür

  // Rol badge rengini döndür
  const getRoleBadgeClass = (role) => {
    const classes = {
      [ROLES.ADMIN]: 'role-badge-admin',
      [ROLES.SATISCI]: 'role-badge-satisci',
      [ROLES.MUHASEBECI]: 'role-badge-muhasebeci',
      [ROLES.MONTAJCI]: 'role-badge-montajci',
      [ROLES.YAZILIMCI]: 'role-badge-yazilimci'
    };
    return classes[role] || '';
  };

  // ======== KULLANICI YÖNETİMİ FONKSİYONLARI SONU ========

  const handleLogout = async () => {
    try {
      // Firebase'den çıkış yap
      await signOut(auth);
      // Firebase otomatik olarak oturumu temizler
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Çıkış hatası:', error);
      // Hata olsa bile ana sayfaya yönlendir
      navigate(ROUTES.LOGIN);
    }
  };


  // Backup fonksiyonu
  const handleExport = async () => {
    try {
      setIsExporting(true);
      const result = await exportData(userRole, user?.uid);
      showNotificationModal(
        'success',
        t('dashboard.backup.exportSuccessTitle'),
        t('dashboard.backup.exportSuccessMessage'),
        {
          customers: result.customers,
          orders: result.orders
        }
      );
    } catch (error) {
      showNotificationModal('error', t('dashboard.backup.exportErrorTitle'), error.message);
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Import modal'ını aç
  const handleImportClick = () => {
    setShowImportModal(true);
    setImportFile(null);
  };

  // Dosya seçimi
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        validateBackupFile(file);
        setImportFile(file);
      } catch (error) {
        showNotificationModal('error', t('dashboard.backup.fileErrorTitle'), error.message);
        event.target.value = '';
      }
    }
  };

  // Import işlemi
  const handleImport = async () => {
    if (!importFile) {
      showNotificationModal('warning', t('dashboard.backup.noFileTitle'), t('dashboard.backup.noFileMessage'));
      return;
    }

    try {
      setIsImporting(true);
      const result = await importData(importFile);

      // Modal'ı kapat
      setShowImportModal(false);
      setImportFile(null);

      // İstatistikleri yeniden yükle
      fetchAllStats();

      // Başarı modal'ını göster
      showNotificationModal(
        'success',
        t('dashboard.backup.importSuccessTitle'),
        t('dashboard.backup.importSuccessMessage'),
        {
          backupCustomers: result.totalCustomers,
          backupOrders: result.totalOrders,
          addedCustomers: result.addedCustomers,
          updatedCustomers: result.updatedCustomers,
          addedOrders: result.addedOrders,
          updatedOrders: result.updatedOrders
        }
      );
    } catch (error) {
      showNotificationModal('error', t('dashboard.backup.importErrorTitle'), error.message);
      console.error('Import error:', error);
    } finally {
      setIsImporting(false);
    }
  };

  // Modal'ı kapat
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
  };

  //Şifre değiştirme form kontrolü (Onay modalını açar)
  const handlePasswordFormSubmit = (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validasyonlar
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('dashboard.password.errors.fillAll'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('dashboard.password.errors.mismatch'));
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError(t('dashboard.password.errors.minLength'));
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError(t('dashboard.password.errors.sameAsOld'));
      return;
    }

    setShowPasswordConfirmModal(true);
  };

  // Asıl şifre değiştirme işlemi (Onaylandıktan sonra çalışır)
  const handleChangePasswordConfirm = async () => {
    setShowPasswordConfirmModal(false);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      setIsChangingPassword(true);

      //Mevcut şifreyi doğrula
      const user = auth.currentUser;
      if (!user || !user.email) {
        setPasswordError(t('dashboard.password.errors.userNotFound'));
        setIsChangingPassword(false);
        return;
      }

      // Mevcut şifreyi doğrula (ayrı try-catch ile)
      try {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
      } catch (authError) {
        console.error('Şifre doğrulama hatası:', authError);

        // Mevcut şifre ile ilgili hatalar
        if (authError.code === 'auth/wrong-password' ||
          authError.code === 'auth/invalid-credential' ||
          authError.code === 'auth/invalid-email' ||
          authError.message?.includes('password') ||
          authError.message?.includes('credential')) {
          setPasswordError(t('dashboard.password.errors.wrongCurrent'));
        } else if (authError.code === 'auth/user-mismatch') {
          setPasswordError(t('dashboard.password.errors.userMismatch'));
        } else if (authError.code === 'auth/requires-recent-login') {
          setPasswordError(t('dashboard.password.errors.requiresRecentLogin'));
        } else {
          setPasswordError(t('dashboard.password.errors.verifyFailed'));
        }
        setIsChangingPassword(false);
        return;
      }

      // Şifreyi güncelle (ayrı try-catch ile)
      try {
        await updatePassword(user, newPassword);
      } catch (updateError) {
        console.error('Şifre güncelleme hatası:', updateError);

        if (updateError.code === 'auth/weak-password') {
          setPasswordError(t('dashboard.password.errors.weakPassword'));
        } else if (updateError.code === 'auth/requires-recent-login') {
          setPasswordError(t('dashboard.password.errors.requiresRecentLogin'));
        } else {
          setPasswordError(t('dashboard.password.errors.updateFailed'));
        }
        setIsChangingPassword(false);
        return;
      }

      // Başarılı
      setPasswordSuccess(t('dashboard.password.success.changed'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // 3 saniye sonra formu kapat
      setTimeout(() => {
        setShowPasswordChange(false);
        setPasswordSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Şifre değiştirme hatası:', error);
      setPasswordError(t('dashboard.password.errors.unexpected'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Kullanıcı Yönetimi Render
  const renderUserManagement = () => {
    return (
      <div className="user-management-content">
        <div className="section-header">
          <h2><i className="fas fa-users-cog"></i> {t('users.title')}</h2>
          <button
            className="btn-add-user"
            onClick={() => setShowAddUserModal(true)}
          >
            <i className="fas fa-user-plus"></i>
            {t('users.addNew')}
          </button>
        </div>

        {isLoadingUsers ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <span>{t('users.loading')}</span>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>{t('users.table.displayName')}</th>
                  <th>{t('users.table.email')}</th>
                  <th>{t('users.table.role')}</th>
                  <th>{t('users.table.createdAt')}</th>
                  <th>{t('users.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.displayName || '-'}</td>
                    <td>{u.email}</td>
                    <td>
                      {editingUserRole === u.id ? (
                        <select
                          value={u.role}
                          onChange={(e) => handleUpdateUserRole(u.id, e.target.value)}
                          onBlur={() => setEditingUserRole(null)}
                          autoFocus
                        >
                          <option value={ROLES.ADMIN}>{getRoleName(ROLES.ADMIN)}</option>
                          <option value={ROLES.SATISCI}>{getRoleName(ROLES.SATISCI)}</option>
                          <option value={ROLES.MUHASEBECI}>{getRoleName(ROLES.MUHASEBECI)}</option>
                          <option value={ROLES.MONTAJCI}>{getRoleName(ROLES.MONTAJCI)}</option>
                          <option value={ROLES.YAZILIMCI}>{getRoleName(ROLES.YAZILIMCI)}</option>
                        </select>
                      ) : (
                        <span
                          className={`role-badge ${getRoleBadgeClass(u.role)}`}
                          onClick={() => u.id !== user?.uid && setEditingUserRole(u.id)}
                          style={{ cursor: u.id !== user?.uid ? 'pointer' : 'default' }}
                          title={u.id !== user?.uid ? t('users.clickToChangeRole') : t('users.cannotChangeOwnRole')}
                        >
                          {getRoleName(u.role)}
                        </span>
                      )}
                    </td>
                    <td>
                      {u.createdAt?.toDate?.()
                        ? new Date(u.createdAt.toDate()).toLocaleDateString(statsLocale)
                        : '-'}
                    </td>
                    <td>
                      {u.id !== user?.uid ? (
                        <button
                          className="btn-delete-user"
                          onClick={() => setDeleteUserModal(u)}
                          title={t('users.deleteUser')}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      ) : (
                        <span className="current-user-badge">{t('users.you')}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="5" className="empty-state">
                      {t('users.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="role-info-section">
          <h3><i className="fas fa-info-circle"></i> {t('users.rolePermissions.title')}</h3>
          <div className="role-info-grid">
            <div className="role-info-card">
              <span className="role-badge role-badge-admin">{getRoleName(ROLES.ADMIN)}</span>
              <p>{t('users.rolePermissions.admin')}</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-satisci">{getRoleName(ROLES.SATISCI)}</span>
              <p>{t('users.rolePermissions.satisci')}</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-muhasebeci">{getRoleName(ROLES.MUHASEBECI)}</span>
              <p>{t('users.rolePermissions.muhasebeci')}</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-montajci">{getRoleName(ROLES.MONTAJCI)}</span>
              <p>{t('users.rolePermissions.montajci')}</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-yazilimci">{getRoleName(ROLES.YAZILIMCI)}</span>
              <p>{t('users.rolePermissions.yazilimci')}</p>
            </div>
          </div>
        </div>

        {/* Satışçı Prim Tablosu */}
        {(() => {
          const salesUsers = users.filter(u => u.role === ROLES.SATISCI);
          if (salesUsers.length === 0) return null;
          return (
            <div className="commission-section">
              <div className="commission-section-header">
                <h3><i className="fas fa-percentage"></i> {t('users.commission.title')}</h3>
                <p className="commission-section-desc">{t('users.commission.description')}</p>
              </div>
              <div className="commission-cards-grid">
                {salesUsers.map((su) => {
                  const history = Array.isArray(su.commissionHistory) ? su.commissionHistory : [];
                  const grouped = groupCommissionsByPeriod(history);
                  return (
                    <div key={su.id} className="commission-user-card">
                      <div className="commission-user-header">
                        <span className="commission-user-name">
                          <i className="fas fa-user-tie"></i>
                          {su.displayName || su.email}
                        </span>
                        <span className="commission-balance">
                          {formatPrice(parseFloat(su.commissionBalance || 0))} {CURRENCY_SYMBOL}
                        </span>
                      </div>
                      <div className="commission-period-row">
                        {[
                          { key: 'daily',   label: t('users.commission.daily'),   data: grouped.daily },
                          { key: 'weekly',  label: t('users.commission.weekly'),  data: grouped.weekly },
                          { key: 'monthly', label: t('users.commission.monthly'), data: grouped.monthly }
                        ].map(({ key, label, data }) => {
                          const retailCount    = data.entries.filter(e => e.type === 'retail').length;
                          const wholesaleCount = data.entries.filter(e => e.type === 'wholesale').length;
                          return (
                            <div key={key} className="commission-period-cell">
                              <span className="commission-period-label">{label}</span>
                              <span className="commission-period-value">{formatPrice(data.total)} {CURRENCY_SYMBOL}</span>
                              <span className="commission-period-count">
                                {retailCount > 0 && (
                                  <span className="commission-type-badge commission-type-retail">
                                    {retailCount} {t('users.commission.retail')}
                                  </span>
                                )}
                                {wholesaleCount > 0 && (
                                  <span className="commission-type-badge commission-type-wholesale">
                                    {wholesaleCount} {t('users.commission.wholesale')}
                                  </span>
                                )}
                                {retailCount === 0 && wholesaleCount === 0 && (
                                  <span>0 {t('users.commission.transactions')}</span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {/* Yıllık aylık grafik */}
                      {(() => {
                        const currentYear = commissionYear;
                        const trend = buildMonthlyCommissionTrend(history, currentYear);
                        const MONTHS_SHORT = t('dashboard.stats.months.short', { returnObjects: true });
                        const chartData = trend.map((item, i) => ({
                          month: Array.isArray(MONTHS_SHORT) ? MONTHS_SHORT[i] : String(i + 1),
                          commission: item.total
                        }));
                        const hasData = trend.some(d => d.total > 0);
                        return (
                          <div className="commission-chart-wrapper">
                            <div className="commission-chart-header">
                              <span className="commission-chart-title">{t('users.commission.monthlyChart', { year: currentYear })}</span>
                              <div className="commission-year-selector">
                                <button
                                  className="commission-year-btn"
                                  onClick={() => setCommissionYear(y => y - 1)}
                                >‹</button>
                                <span>{currentYear}</span>
                                <button
                                  className="commission-year-btn"
                                  onClick={() => setCommissionYear(y => Math.min(y + 1, new Date().getFullYear()))}
                                >›</button>
                              </div>
                            </div>
                            {hasData ? (
                              <ResponsiveContainer width="100%" height={160}>
                                <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                                  <XAxis dataKey="month" stroke="#6b7280" tick={{ fontSize: 11 }} />
                                  <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                                  <Tooltip formatter={(v) => [`${formatPrice(v)} ${CURRENCY_SYMBOL}`, t('users.commission.earned')]} />
                                  <Line type="monotone" dataKey="commission" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3, fill: '#7c3aed' }} activeDot={{ r: 5 }} />
                                </LineChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="commission-no-data">{t('users.commission.noData', { year: currentYear })}</div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  const renderDashboardContent = () => {
        // Satışcı dashboard'a erişemez
        if (!permissions?.canAccessDashboard) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>{t('dashboard.accessDenied.title')}</h2>
              <p>{t('dashboard.accessDenied.dashboard')}</p>
              <p>{t('dashboard.accessDenied.dashboardHint')}</p>
            </div>
          );
        }
        return (
          <div className="dashboard-content">
            <div className="welcome-card">
              <h2>{t('dashboard.welcome.title')}</h2>
              <p>{t('dashboard.welcome.loginSuccess')}</p>
              <p>{t('dashboard.welcome.description')}</p>
              {user?.email && (
                <div className="user-info">
                  <p><strong>{t('dashboard.welcome.loggedInAs')}</strong> {user.email}</p>
                  {userRole && (
                    <p><strong>{t('dashboard.welcome.role')}</strong> <span className={`role-badge ${getRoleBadgeClass(userRole)}`}>{getRoleName(userRole)}</span></p>
                  )}
                </div>
              )}
            </div>

            <div className="cache-info-banner">
              <div className="cache-info-text">
                <i className="fas fa-history"></i>
                <span>
                  <Trans
                    i18nKey="dashboard.stats.lastUpdated"
                    values={{
                      time: statsLastUpdated ? statsLastUpdated.toLocaleTimeString(statsLocale) : '...'
                    }}
                    components={{ strong: <strong /> }}
                  />
                </span>
              </div>
              <button 
                className="refresh-stats-btn" 
                onClick={() => fetchAllStats(true)} 
                disabled={isLoadingStats}
              >
                {isLoadingStats ? (
                  <><i className="fas fa-spinner fa-spin"></i> {t('dashboard.stats.refreshing')}</>
                ) : (
                  <><i className="fas fa-sync-alt"></i> {t('dashboard.stats.refresh')}</>
                )}
              </button>
            </div>

            {/* Teslimat Hatırlatıcısı */}
            {(hasDeliveryReminders(deliveryReminders) || hasDeliveryReminders(dealerDeliveryReminders)) && (
                <div className="delivery-reminders-section">
                  <h3><i className="fas fa-bell"></i> {t('dashboard.stats.deliveryReminder.title')}</h3>

                  <div className="dashboard-stats-tabs delivery-reminders-tabs">
                    <button
                      type="button"
                      className={`stats-tab-btn ${statsTab === 'retail' ? 'active' : ''}`}
                      onClick={() => setStatsTab('retail')}
                    >
                      <i className="fas fa-store"></i> {t('dashboard.stats.tabs.retailShort')}
                    </button>
                    <button
                      type="button"
                      className={`stats-tab-btn ${statsTab === 'dealer' ? 'active' : ''}`}
                      onClick={() => setStatsTab('dealer')}
                    >
                      <i className="fas fa-industry"></i> {t('dashboard.stats.tabs.dealerShort')}
                    </button>
                  </div>

                  <div className="delivery-reminders-block">
                    {statsTab === 'retail' ? (
                      hasDeliveryReminders(deliveryReminders) ? (
                        <DeliveryReminderGrid reminders={deliveryReminders} t={t} />
                      ) : (
                        <p className="delivery-reminders-empty">{t('dashboard.stats.deliveryReminder.retailEmpty')}</p>
                      )
                    ) : hasDeliveryReminders(dealerDeliveryReminders) ? (
                      <DeliveryReminderGrid reminders={dealerDeliveryReminders} showDealerMeta t={t} />
                    ) : (
                      <p className="delivery-reminders-empty">{t('dashboard.stats.deliveryReminder.dealerEmpty')}</p>
                    )}
                  </div>
                </div>
              )}

            {/* Sekme Seçici - İstatistik Tipi */}
            <div className="dashboard-stats-tabs">
              <button
                className={`stats-tab-btn ${statsTab === 'retail' ? 'active' : ''}`}
                onClick={() => setStatsTab('retail')}
              >
                <i className="fas fa-store"></i> {t('dashboard.stats.tabs.retail')}
              </button>
              <button
                className={`stats-tab-btn ${statsTab === 'dealer' ? 'active' : ''}`}
                onClick={() => setStatsTab('dealer')}
              >
                <i className="fas fa-industry"></i> {t('dashboard.stats.tabs.dealer')}
              </button>
            </div>

            {/* İstatistikler (Perakende veya Bayi) */}
            <div className="stats-section">
              <h3>{statsTab === 'retail' ? t('dashboard.stats.sections.customer') : t('dashboard.stats.sections.dealer')}</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className={statsTab === 'retail' ? "fas fa-users" : "fas fa-cubes"}></i>
                  </div>
                  <div className="stat-content">
                    <h3>{statsTab === 'retail' ? t('dashboard.stats.cards.totalCustomers') : t('dashboard.stats.cards.totalOrders')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : (statsTab === 'retail' ? customerStats.total : dealerStats.total)}
                    </div>
                    <small>{statsTab === 'retail' ? t('dashboard.stats.cards.allRegisteredCustomers') : t('dashboard.stats.cards.allOrderLists')}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className={statsTab === 'retail' ? "fas fa-user-check" : "fas fa-list-alt"}></i>
                  </div>
                  <div className="stat-content">
                    <h3>{statsTab === 'retail' ? t('dashboard.stats.cards.activeCustomers') : t('dashboard.stats.cards.totalPieces')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : (statsTab === 'retail' ? customerStats.active : dealerStats.totalProducts)}
                    </div>
                    <small>{statsTab === 'retail' ? t('dashboard.stats.cards.newOrderStatus') : t('dashboard.stats.cards.orderItemCount')}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-day"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.stats.periods.today')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : (statsTab === 'retail' ? customerStats.today : dealerStats.today)}
                    </div>
                    <small>{t('dashboard.stats.cards.orderedToday')}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-week"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.stats.periods.thisWeek')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : (statsTab === 'retail' ? customerStats.thisWeek : dealerStats.thisWeek)}
                    </div>
                    <small>{t('dashboard.stats.cards.orderedLast7Days')}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.stats.periods.thisMonth')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : (statsTab === 'retail' ? customerStats.thisMonth : dealerStats.thisMonth)}
                    </div>
                    <small>{t('dashboard.stats.cards.orderedThisMonth')}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.stats.periods.thisYear')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : (statsTab === 'retail' ? customerStats.thisYear : dealerStats.thisYear)}
                    </div>
                    <small>{t('dashboard.stats.cards.orderedThisYear')}</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Aylık Müşteri Trendi (Sadece Perakende modunda anlamlı ama bayi için de sipariş listesi trendi yansıtılır) */}
            {statsTab === 'retail' && (
              <div className="stats-section">
                <div className="section-header">
                  <h3>{t('dashboard.stats.charts.monthlyCustomerTrend', { year: currentYear })}</h3>
                </div>
                <div className="chart-card">
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={localizedMonthlyCustomerTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                      <XAxis dataKey="month" stroke="#b04309" />
                      <YAxis allowDecimals={false} stroke="#b04309" />
                      <Tooltip formatter={(value) => [`${value}`, t('dashboard.stats.charts.customer')]} />
                      <Line type="monotone" dataKey="count" name={t('dashboard.stats.charts.customer')} stroke="#b04309" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Aylık Sipariş Listesi/Sipariş Trendi */}
            <div className="stats-section">
              <div className="section-header">
                <h3>
                  {statsTab === 'retail'
                    ? t('dashboard.stats.charts.monthlyOrderTrend', { year: currentYear })
                    : t('dashboard.stats.charts.monthlyDealerOrderListTrend', { year: currentYear })}
                </h3>
              </div>
              <div className="chart-card">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={statsTab === 'retail' ? localizedMonthlyOrderTrend : localizedDealerMonthlyOrderTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                    <XAxis dataKey="month" stroke="#b04309" />
                    <YAxis allowDecimals={false} stroke="#b04309" />
                    <Tooltip formatter={(value) => [`${value}`, statsTab === 'retail' ? t('dashboard.stats.charts.order') : t('dashboard.stats.charts.orderList')]} />
                    <Line type="monotone" dataKey="count" name={statsTab === 'retail' ? t('dashboard.stats.charts.order') : t('dashboard.stats.charts.orderList')} stroke="#b04309" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Bayi modunda ek: Aylık Sipariş Kalemi Trendi */}
            {statsTab === 'dealer' && (
              <div className="stats-section">
                <div className="section-header">
                  <h3>{t('dashboard.stats.charts.monthlyOrderTrend', { year: currentYear })}</h3>
                </div>
                <div className="chart-card">
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={localizedDealerMonthlyOrderItemTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                      <XAxis dataKey="month" stroke="#7c3aed" />
                      <YAxis allowDecimals={false} stroke="#7c3aed" />
                      <Tooltip formatter={(value) => [`${value}`, t('dashboard.stats.charts.order')]} />
                      <Line
                        type="monotone"
                        dataKey="count"
                        name={t('dashboard.stats.charts.order')}
                        stroke="#7c3aed"
                        strokeWidth={3}
                        dot={{ r: 4, fill: '#7c3aed' }}
                        activeDot={{ r: 6, fill: '#5b21b6' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Aylık Kazanç Trendi */}
            <div className="stats-section">
              <div className="section-header">
                <h3>{t('dashboard.stats.charts.monthlyEarningsTrend', { year: currentYear })}</h3>
              </div>
              <div className="chart-card">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={statsTab === 'retail' ? localizedMonthlyEarningsTrend : localizedDealerMonthlyEarningsTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                    <XAxis dataKey="month" stroke="#b04309" />
                    <YAxis
                      stroke="#b04309"
                      tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}K` : value}
                    />
                    <Tooltip
                      formatter={(value) => [`${formatPrice(value)} ${CURRENCY_SYMBOL}`, t('dashboard.stats.charts.earnings')]}
                      labelFormatter={(label) => `${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="earnings"
                      name={t('dashboard.stats.charts.earnings')}
                      stroke="#2e7d32"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#2e7d32' }}
                      activeDot={{ r: 6, fill: '#1b5e20' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Aylık Net Kazanç Trendi */}
            <div className="stats-section">
              <div className="section-header">
                <h3>{t('dashboard.stats.charts.monthlyNetProfitTrend', { year: currentYear })}</h3>
              </div>
              <div className="chart-card">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={statsTab === 'retail' ? localizedMonthlyNetProfitTrend : localizedDealerMonthlyNetProfitTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                    <XAxis dataKey="month" stroke="#b04309" />
                    <YAxis
                      stroke="#b04309"
                      tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}K` : value}
                    />
                    <Tooltip
                      formatter={(value) => [`${formatPrice(value)} ${CURRENCY_SYMBOL}`, t('dashboard.stats.charts.netProfit')]}
                      labelFormatter={(label) => `${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="netProfit"
                      name={t('dashboard.stats.charts.netProfit')}
                      stroke="#10b981"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#10b981' }}
                      activeDot={{ r: 6, fill: '#059669' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Aylık Prim Trendi */}
            {users.some(u => u.role === ROLES.SATISCI) && (
              <div className="stats-section">
                <div className="section-header">
                  <h3>{t('dashboard.stats.charts.monthlyCommissionTrend', { year: currentYear })}</h3>
                </div>
                <div className="chart-card">
                  {localizedCommissionTrend.some(d => d.commission > 0) ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={localizedCommissionTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                        <XAxis dataKey="month" stroke="#7c3aed" />
                        <YAxis
                          stroke="#7c3aed"
                          tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}K` : value}
                        />
                        <Tooltip
                          formatter={(value) => [`${formatPrice(value)} ${CURRENCY_SYMBOL}`, t('users.commission.earned')]}
                          labelFormatter={(label) => `${label}`}
                        />
                        <Line
                          type="monotone"
                          dataKey="commission"
                          name={t('users.commission.earned')}
                          stroke="#7c3aed"
                          strokeWidth={3}
                          dot={{ r: 4, fill: '#7c3aed' }}
                          activeDot={{ r: 6, fill: '#5b21b6' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0', fontSize: '14px' }}>
                      {t('users.commission.noData', { year: currentYear })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Gelir İstatistikleri */}
            <div className="stats-section revenue-stats">
              <h3>{t('dashboard.stats.sections.revenue')}</h3>
              <div className="stats-grid">
                {statsTab === 'retail' && (
                  <div className="stat-card">
                    <div className="stat-icon">
                      <i className="fas fa-coins"></i>
                    </div>
                    <div className="stat-content">
                      <h3>{t('dashboard.stats.cards.expectedRevenue')}</h3>
                      <div className="stat-number">
                        {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.expectedTotal)} ${CURRENCY_SYMBOL}`}
                      </div>
                      <small>{t('dashboard.stats.cards.allOrdersTotal')}</small>
                    </div>
                  </div>
                )}

                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-wallet"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{statsTab === 'retail' ? t('dashboard.stats.cards.totalRevenue') : t('dashboard.stats.cards.totalOrders')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.total : dealerRevenueStats.totalAmount)} ${CURRENCY_SYMBOL}`}
                    </div>
                    <small>{statsTab === 'retail' ? t('dashboard.stats.cards.receivedPayments') : t('dashboard.stats.cards.allOrderLists')}</small>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.stats.cards.totalNetRevenue')}</h3>
                    <div className="stat-number" style={{ color: '#16a34a' }}>
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.totalNetProfit : dealerRevenueStats.totalNetProfit)} ${CURRENCY_SYMBOL}`}
                    </div>
                    <small>{t('dashboard.stats.cards.paymentsMinusCosts')}</small>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
                    <i className="fas fa-hand-holding-usd"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.stats.cards.potentialNetProfit')}</h3>
                    <div className="stat-number" style={{ color: '#d97706' }}>
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.potentialNetProfit : dealerRevenueStats.potentialNetProfit)} ${CURRENCY_SYMBOL}`}
                    </div>
                    <small>{t('dashboard.stats.cards.totalSalesMinusCost')}</small>
                  </div>
                </div>

                {statsTab === 'dealer' && (
                  <div className="stat-card">
                    <div className="stat-icon">
                      <i className="fas fa-coins"></i>
                    </div>
                    <div className="stat-content">
                      <h3>{t('dashboard.stats.cards.collected')}</h3>
                      <div className="stat-number">
                        {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(dealerRevenueStats.paidAmount)} ${CURRENCY_SYMBOL}`}
                      </div>
                      <small>{t('dashboard.stats.cards.receivedPayments')}</small>
                    </div>
                  </div>
                )}

                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-hourglass-half"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{statsTab === 'retail' ? t('dashboard.stats.cards.remainingPayments') : t('dashboard.stats.cards.openBalance')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.remainingTotal : dealerRevenueStats.remainingAmount)} ${CURRENCY_SYMBOL}`}
                    </div>
                    <small>{t('dashboard.stats.cards.toBeCollected')}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-day"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.stats.periods.today')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.today : dealerRevenueStats.today)} ${CURRENCY_SYMBOL}`}
                    </div>
                    <div className="profit-line">
                      <span>{t('dashboard.stats.cards.profit')}</span>
                      <span className="profit-amount">{isLoadingStats ? '...' : `${formatPrice(statsTab === 'retail' ? revenueStats.todayProfit : dealerRevenueStats.todayProfit)} ${CURRENCY_SYMBOL}`}</span>
                    </div>
                    <small>{t('dashboard.stats.cards.todayRevenue')}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-week"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.stats.periods.thisWeek')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.thisWeek : dealerRevenueStats.thisWeek)} ${CURRENCY_SYMBOL}`}
                    </div>
                    <div className="profit-line">
                      <span>{t('dashboard.stats.cards.profit')}</span>
                      <span className="profit-amount">{isLoadingStats ? '...' : `${formatPrice(statsTab === 'retail' ? revenueStats.thisWeekProfit : dealerRevenueStats.thisWeekProfit)} ${CURRENCY_SYMBOL}`}</span>
                    </div>
                    <small>{t('dashboard.stats.cards.last7Days')}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.stats.periods.thisMonth')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.thisMonth : dealerRevenueStats.thisMonth)} ${CURRENCY_SYMBOL}`}
                    </div>
                    <div className="profit-line">
                      <span>{t('dashboard.stats.cards.profit')}</span>
                      <span className="profit-amount">{isLoadingStats ? '...' : `${formatPrice(statsTab === 'retail' ? revenueStats.thisMonthProfit : dealerRevenueStats.thisMonthProfit)} ${CURRENCY_SYMBOL}`}</span>
                    </div>
                    <small>{t('dashboard.stats.cards.thisMonthRevenue')}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-check"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.stats.periods.thisYear')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(statsTab === 'retail' ? revenueStats.thisYear : dealerRevenueStats.thisYear)} ${CURRENCY_SYMBOL}`}
                    </div>
                    <div className="profit-line">
                      <span>{t('dashboard.stats.cards.profit')}</span>
                      <span className="profit-amount">{isLoadingStats ? '...' : `${formatPrice(statsTab === 'retail' ? revenueStats.thisYearProfit : dealerRevenueStats.thisYearProfit)} ${CURRENCY_SYMBOL}`}</span>
                    </div>
                    <small>{t('dashboard.stats.cards.thisYearRevenue')}</small>
                  </div>
                </div>
              </div>

              {/* Tedarikçi Alımları & Nakit Akışı Durumu (Sadece Bayi İçin Gosterilir) */}
              {statsTab === 'dealer' && (
                <>
                  <h4 className="payment-type-title" style={{ marginTop: '40px' }}>
                    <i className="fas fa-truck-loading"></i> {t('dashboard.stats.sections.supplierCashFlow')}
                  </h4>
                  <div className="stats-grid payment-type-stats" style={{ marginTop: '15px' }}>
                    <div className="stat-card" style={{ borderLeft: '4px solid #ef4444' }}>
                      <div className="stat-icon" style={{ background: '#fee2e2', color: '#ef4444' }}>
                        <i className="fas fa-hand-holding-usd"></i>
                      </div>
                      <div className="stat-content">
                        <h3>{t('dashboard.stats.cards.supplierDebt')}</h3>
                        <div className="stat-number" style={{ color: '#ef4444' }}>
                          {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(dealerRevenueStats.supplierTotalDebt || 0)} ${CURRENCY_SYMBOL}`}
                        </div>
                        <small>{t('dashboard.stats.cards.supplierDebtHint')}</small>
                      </div>
                    </div>

                    <div className="stat-card" style={{ borderLeft: '4px solid #3b82f6' }}>
                      <div className="stat-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
                        <i className="fas fa-shopping-basket"></i>
                      </div>
                      <div className="stat-content">
                        <h3>{t('dashboard.stats.cards.totalPurchaseVolume')}</h3>
                        <div className="stat-number" style={{ color: '#3b82f6' }}>
                          {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(dealerRevenueStats.supplierTotalPurchases || 0)} ${CURRENCY_SYMBOL}`}
                        </div>
                        <small>{t('dashboard.stats.cards.totalPurchaseVolumeHint')}</small>
                      </div>
                    </div>

                    <div className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
                      <div className="stat-icon" style={{ background: '#d1fae5', color: '#10b981' }}>
                        <i className="fas fa-check-double"></i>
                      </div>
                      <div className="stat-content">
                        <h3>{t('dashboard.stats.cards.supplierPaid')}</h3>
                        <div className="stat-number" style={{ color: '#10b981' }}>
                          {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(dealerRevenueStats.supplierTotalPaid || 0)} ${CURRENCY_SYMBOL}`}
                        </div>
                        <small>{t('dashboard.stats.cards.supplierPaidHint')}</small>
                      </div>
                    </div>

                    <div className="stat-card" style={{ 
                      borderLeft: '4px solid ' + ((dealerRevenueStats.remainingAmount - (dealerRevenueStats.supplierTotalDebt || 0)) >= 0 ? '#10b981' : '#ef4444')
                    }}>
                      <div className="stat-icon" style={{ 
                        background: (dealerRevenueStats.remainingAmount - (dealerRevenueStats.supplierTotalDebt || 0)) >= 0 ? '#d1fae5' : '#fee2e2', 
                        color: (dealerRevenueStats.remainingAmount - (dealerRevenueStats.supplierTotalDebt || 0)) >= 0 ? '#10b981' : '#ef4444' 
                      }}>
                        <i className="fas fa-balance-scale"></i>
                      </div>
                      <div className="stat-content">
                        <h3>{t('dashboard.stats.cards.netCurrentBalance')}</h3>
                        <div className="stat-number" style={{ 
                          color: (dealerRevenueStats.remainingAmount - (dealerRevenueStats.supplierTotalDebt || 0)) >= 0 ? '#10b981' : '#ef4444' 
                        }}>
                          {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(dealerRevenueStats.remainingAmount - (dealerRevenueStats.supplierTotalDebt || 0))} ${CURRENCY_SYMBOL}`}
                        </div>
                        <small>{t('dashboard.stats.cards.netCurrentBalanceHint')}</small>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Ödeme Tipine Göre Günlük Gelirler (Sadece Perakende İçin Gosterilir) */}
              {statsTab === 'retail' && (
                <>
                  <h4 className="payment-type-title"><i className="fas fa-receipt"></i> {t('dashboard.stats.paymentType.title')}</h4>
                  <div className="stats-grid payment-type-stats">
                    <div className="stat-card payment-cash">
                      <div className="stat-icon">
                        <i className="fas fa-money-bill-wave"></i>
                      </div>
                      <div className="stat-content">
                        <h3>{t('dashboard.stats.paymentType.todayCash')}</h3>
                        <div className="stat-number">
                          {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.todayCash)} ${CURRENCY_SYMBOL}`}
                        </div>
                        <small>{t('dashboard.stats.paymentType.todayCashHint')}</small>
                      </div>
                    </div>
                    <div className="stat-card payment-iban">
                      <div className="stat-icon">
                        <i className="fas fa-university"></i>
                      </div>
                      <div className="stat-content">
                        <h3>{t('dashboard.stats.paymentType.todayIban')}</h3>
                        <div className="stat-number">
                          {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.todayIban)} ${CURRENCY_SYMBOL}`}
                        </div>
                        <small>{t('dashboard.stats.paymentType.todayIbanHint')}</small>
                      </div>
                    </div>
                    <div className="stat-card payment-credit-card">
                      <div className="stat-icon">
                        <i className="fas fa-credit-card"></i>
                      </div>
                      <div className="stat-content">
                        <h3>{t('dashboard.stats.paymentType.yesterdayCreditCard')}</h3>
                        <div className="stat-number">
                          {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.yesterdayCreditCard)} ${CURRENCY_SYMBOL}`}
                        </div>
                        <small>{t('dashboard.stats.paymentType.yesterdayCreditCardHint')}</small>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Geçen Yıl Karşılaştırması (Sadece Perakende İçin Gosterilir) */}
            {statsTab === 'retail' && (
              <div className="stats-section">
                <h3>{t('dashboard.stats.lastYear.title', { year: currentYear - 1 })}</h3>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <i className="fas fa-history"></i>
                    </div>
                    <div className="stat-content">
                      <h3>{t('dashboard.stats.lastYear.today')}</h3>
                      <div className="stat-number">
                        {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.today}
                      </div>
                      <small>{t('dashboard.stats.lastYear.todayHint')}</small>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <i className="fas fa-history"></i>
                    </div>
                    <div className="stat-content">
                      <h3>{t('dashboard.stats.lastYear.thisWeek')}</h3>
                      <div className="stat-number">
                        {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.thisWeek}
                      </div>
                      <small>{t('dashboard.stats.lastYear.thisWeekHint')}</small>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <i className="fas fa-history"></i>
                    </div>
                    <div className="stat-content">
                      <h3>{t('dashboard.stats.lastYear.thisMonth')}</h3>
                      <div className="stat-number">
                        {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.thisMonth}
                      </div>
                      <small>{t('dashboard.stats.lastYear.thisMonthHint')}</small>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">
                      <i className="fas fa-chart-bar"></i>
                    </div>
                    <div className="stat-content">
                      <h3>{t('dashboard.stats.lastYear.total')}</h3>
                      <div className="stat-number">
                        {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.total}
                      </div>
                      <small>{t('dashboard.stats.lastYear.totalHint')}</small>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Backup/Restore Bölümü */}
            <div className="backup-section">
              <h3>{t('dashboard.backup.title')}</h3>
              <div className="backup-grid">
                <div className="backup-card">
                  <div className="backup-icon">📥</div>
                  <h4>{t('dashboard.backup.exportTitle')}</h4>
                  <p>{t('dashboard.backup.exportDescription')}</p>
                  <button
                    onClick={handleExport}
                    disabled={isExporting || isLoadingStats}
                    className="backup-btn export-btn"
                  >
                    {isExporting ? (
                      <span>
                        <div className="btn-spinner"></div>
                        {t('dashboard.backup.exporting')}
                      </span>
                    ) : (
                      <>
                        <i className="fas fa-download"></i>
                        {t('dashboard.backup.exportButton')}
                      </>
                    )}
                  </button>
                </div>

                <div className="backup-card">
                  <div className="backup-icon">📤</div>
                  <h4>{t('dashboard.backup.importTitle')}</h4>
                  <p>{t('dashboard.backup.importDescription')}</p>
                  <button
                    onClick={handleImportClick}
                    disabled={isImporting || isLoadingStats}
                    className="backup-btn import-btn"
                  >
                    <i className="fas fa-upload"></i>
                    {t('dashboard.backup.importButton')}
                  </button>
                </div>
              </div>

              <div className="backup-info">
                <i className="fas fa-info-circle"></i>
                <p>
                  <Trans i18nKey="dashboard.backup.importantNotice" />
                </p>
              </div>
            </div>

            {/* Firma Bilgileri Bölümü */}
            <div className="company-info-section">
              <div className="company-info-header">
                <h3>
                  <i className="fas fa-building"></i>
                  {t('dashboard.company.title')}
                </h3>
                <button
                  className="btn-toggle-company-info"
                  onClick={() => {
                    setShowCompanyInfo(!showCompanyInfo);
                    setCompanyError(null);
                    setCompanySuccess(false);
                    if (!showCompanyInfo) {
                      handleCancelCompany();
                    }
                  }}
                >
                  {showCompanyInfo ? (
                    <>
                      <i className="fas fa-chevron-up"></i>
                      {t('common.hide')}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-chevron-down"></i>
                      {t('common.show')}
                    </>
                  )}
                </button>
              </div>

              {showCompanyInfo && (
                <div className="company-info-content">
                  {companyError && (
                    <div className="company-message error">
                      <i className="fas fa-exclamation-circle"></i>
                      {companyError}
                    </div>
                  )}

                  {companySuccess && (
                    <div className="company-message success">
                      <i className="fas fa-check-circle"></i>
                      {companySuccess}
                    </div>
                  )}

                  <form onSubmit={handleSaveCompany} className="company-form">
                    <div className="form-group">
                      <label htmlFor="companyName">
                        <i className="fas fa-building"></i>
                        {t('dashboard.company.nameLabel')}
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        value={companyForm.name}
                        onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                        placeholder={t('dashboard.company.namePlaceholder')}
                        required
                        disabled={isLoadingCompanies}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="companyPhone">
                        <i className="fas fa-phone"></i>
                        {t('dashboard.company.phoneLabel')}
                      </label>
                      <input
                        type="text"
                        id="companyPhone"
                        value={companyForm.phone}
                        onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                        placeholder={t('dashboard.company.phonePlaceholder')}
                        required
                        disabled={isLoadingCompanies}
                      />
                      <small>{t('dashboard.company.phoneHint')}</small>
                    </div>

                    <div className="company-form-actions">
                      <button
                        type="submit"
                        className="btn-save-company"
                        disabled={isLoadingCompanies}
                      >
                        {isLoadingCompanies ? (
                          <>
                            <div className="btn-spinner"></div>
                            {t('common.saving')}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save"></i>
                            {editingCompany ? t('common.update') : t('common.add')}
                          </>
                        )}
                      </button>
                      {editingCompany && (
                        <button
                          type="button"
                          className="btn-cancel-company"
                          onClick={handleCancelCompany}
                          disabled={isLoadingCompanies}
                        >
                          {t('common.cancel')}
                        </button>
                      )}
                    </div>
                  </form>

                  {/* Firma Listesi */}
                  <div className="companies-list">
                    <h4>{t('dashboard.company.registeredCompanies')}</h4>
                    {isLoadingCompanies ? (
                      <div className="loading-companies">
                        <div className="stat-spinner"></div>
                        <span>{t('common.loading')}</span>
                      </div>
                    ) : companies.length === 0 ? (
                      <div className="no-companies">
                        <p>{t('dashboard.company.noCompanies')}</p>
                      </div>
                    ) : (
                      <div className="companies-table-wrapper">
                        <table className="companies-table">
                          <thead>
                            <tr>
                              <th>{t('dashboard.company.tableName')}</th>
                              <th>{t('dashboard.company.tablePhone')}</th>
                              <th>{t('dashboard.company.tableActions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {companies.map((company) => (
                              <tr key={company.id}>
                                <td>{company.name}</td>
                                <td>{company.phone}</td>
                                <td>
                                  <div className="company-actions">
                                    <button
                                      onClick={() => handleEditCompany(company)}
                                      className="edit-company-btn"
                                      disabled={isLoadingCompanies}
                                      title={t('common.edit')}
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteCompanyClick(company)}
                                      className="delete-company-btn"
                                      disabled={isLoadingCompanies}
                                      title={t('common.delete')}
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="company-info-info">
                    <i className="fas fa-info-circle"></i>
                    <div>
                      <strong>{t('common.infoLabel')}</strong>
                      <ul>
                        <li>{t('dashboard.company.infoTip1')}</li>
                        <li>{t('dashboard.company.infoTip2')}</li>
                        <li>{t('dashboard.company.infoTip3')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Şifre Değiştirme Bölümü */}
            <div className="password-change-section">
              <div className="password-change-header">
                <h3>
                  <i className="fas fa-key"></i>
                  {t('dashboard.password.title')}
                </h3>
                <button
                  className="btn-toggle-password-change"
                  onClick={() => {
                    setShowPasswordChange(!showPasswordChange);
                    setPasswordError('');
                    setPasswordSuccess('');
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                >
                  {showPasswordChange ? (
                    <>
                      <i className="fas fa-chevron-up"></i>
                      {t('common.hide')}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-chevron-down"></i>
                      {t('common.show')}
                    </>
                  )}
                </button>
              </div>

              {showPasswordChange && (
                <div className="password-change-content">
                  {passwordError && (
                    <div className="password-message error">
                      <i className="fas fa-exclamation-circle"></i>
                      {passwordError}
                    </div>
                  )}

                  {passwordSuccess && (
                    <div className="password-message success">
                      <i className="fas fa-check-circle"></i>
                      {passwordSuccess}
                    </div>
                  )}

                  <form onSubmit={handlePasswordFormSubmit} className="password-change-form">
                    <div className="form-group">
                      <label htmlFor="currentPassword">
                        <i className="fas fa-lock"></i>
                        {t('dashboard.password.currentLabel')}
                      </label>
                      <input
                        type="password"
                        id="currentPassword"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder={t('dashboard.password.currentPlaceholder')}
                        required
                        disabled={isChangingPassword}
                        autoComplete="current-password"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="newPassword">
                        <i className="fas fa-key"></i>
                        {t('dashboard.password.newLabel')}
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder={t('dashboard.password.newPlaceholder')}
                        required
                        disabled={isChangingPassword}
                        autoComplete="new-password"
                        minLength={6}
                      />
                      <small>{t('dashboard.password.newHint')}</small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword">
                        <i className="fas fa-key"></i>
                        {t('dashboard.password.confirmLabel')}
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder={t('dashboard.password.confirmPlaceholder')}
                        required
                        disabled={isChangingPassword}
                        autoComplete="new-password"
                        minLength={6}
                      />
                    </div>

                    <div className="password-change-actions">
                      <button
                        type="submit"
                        className="btn-change-password"
                        disabled={isChangingPassword}
                      >
                        {isChangingPassword ? (
                          <>
                            <div className="btn-spinner"></div>
                            {t('dashboard.password.changing')}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save"></i>
                            {t('dashboard.password.changeButton')}
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn-cancel-password"
                        onClick={() => {
                          setShowPasswordChange(false);
                          setPasswordError('');
                          setPasswordSuccess('');
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                        disabled={isChangingPassword}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </form>

                  <div className="password-change-info">
                    <i className="fas fa-info-circle"></i>
                    <div>
                      <strong>{t('dashboard.password.securityNotes')}</strong>
                      <ul>
                        <li>{t('dashboard.password.minLength')}</li>
                        <li>{t('dashboard.password.strongPassword')}</li>
                        <li>{t('dashboard.password.sessionStays')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
  };

  if (loading) {
    return (
      <div className="dashboard-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#fcfcfc' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="stat-spinner" style={{ width: '40px', height: '40px', borderWidth: '4px', margin: '0 auto 15px' }}></div>
          <h2 style={{ color: '#555', fontSize: '18px', fontWeight: '500' }}>{t('auth.checkingPermissions')}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-top">
          <h1>{t('dashboard.title')}</h1>
          <div className="header-actions">
            <LanguageSwitcher variant="header" />
            {user?.email && (
              <span className="user-email">{user.email}</span>
            )}
            <button onClick={handleLogout} className="logout-button">
              {t('auth.logout')}
            </button>
          </div>
        </div>

        <div className="header-bottom">
          <nav className="dashboard-nav">
            {/* Ana Sayfa - Satışcı hariç herkes */}
            {permissions?.canAccessDashboard && (
              <button
                className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setActiveTab('dashboard')}
              >
                {t('dashboard.nav.home')}
              </button>
            )}

            {/* Bayi Listesi */}
            {permissions?.canAccessDealers && (
              <button
                className={`nav-btn ${activeTab === 'dealers' ? 'active' : ''}`}
                onClick={() => setActiveTab('dealers')}
              >
                <i className="fas fa-store"></i> {t('dashboard.nav.dealerList')}
              </button>
            )}

            {/* Müşteri Listesi - Herkes (Montajcı sadece montaj durumundakiler) */}
            <button
              className={`nav-btn ${activeTab === 'customers' ? 'active' : ''}`}
              onClick={() => setActiveTab('customers')}
            >
              {isMontajci ? t('dashboard.nav.installationList') : t('dashboard.nav.customerList')}
            </button>

            {/* Ürün Listesi */}
            {permissions?.canAccessProducts && (
              <button
                className={`nav-btn ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => setActiveTab('products')}
              >
                <i className="fas fa-tags"></i> {t('dashboard.nav.productList')}
              </button>
            )}

            {/* Giderler */}
            {permissions?.canAccessExpenses && (
              <button
                className={`nav-btn ${activeTab === 'expenses' ? 'active' : ''}`}
                onClick={() => setActiveTab('expenses')}
              >
                <i className="fas fa-receipt"></i> {t('dashboard.nav.expenses')}
              </button>
            )}

            {/* Kullanıcı Yönetimi - Sadece Admin */}
            {permissions?.canManageUsers && (
              <button
                className={`nav-btn ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <i className="fas fa-users-cog"></i> {t('dashboard.nav.users')}
              </button>
            )}

            {/* GEÇİCİ DEVRE DIŞI - Hesaplamalar sekmesi kaldırıldı
            {isAdmin && (
              <button
                className={`nav-btn ${activeTab === 'accounting' ? 'active' : ''}`}
                onClick={() => setActiveTab('accounting')}
              >
                <i className="fas fa-calculator"></i> Hesaplamalar
              </button>
            )}
            */}
          </nav>
        </div>
      </header>

      <main className="dashboard-main">
        {/* Dashboard Tab */}
        <div style={{ display: activeTab === 'dashboard' ? 'block' : 'none' }}>
          {!permissions?.canAccessDashboard ? (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>{t('dashboard.accessDenied.title')}</h2>
              <p>{t('dashboard.accessDenied.dashboard')}</p>
              <p>{t('dashboard.accessDenied.dashboardHint')}</p>
            </div>
          ) : (
            renderDashboardContent()
          )}
        </div>

        {/* Müşteri Listesi (Customers) Tab */}
        {hasMountedCustomers && (
          <div style={{ display: activeTab === 'customers' ? 'block' : 'none' }}>
            <CustomerList onCustomerChange={handleCustomerChange} userRole={userRole} userId={user?.uid} />
          </div>
        )}

        {/* Bayi Listesi (Dealers) Tab */}
        {hasMountedDealers && (
          <div style={{ display: activeTab === 'dealers' ? 'block' : 'none' }}>
            {!permissions?.canAccessDealers ? (
              <div className="access-denied-card">
                <i className="fas fa-lock"></i>
                <h2>{t('dashboard.accessDenied.title')}</h2>
                <p>{t('dashboard.accessDenied.page')}</p>
              </div>
            ) : (
              <DealerList onCustomerChange={handleCustomerChange} />
            )}
          </div>
        )}

        {/* Ürün Listesi (Products) Tab */}
        {hasMountedProducts && (
          <div style={{ display: activeTab === 'products' ? 'block' : 'none' }}>
            {!permissions?.canAccessProducts ? (
              <div className="access-denied-card">
                <i className="fas fa-lock"></i>
                <h2>{t('dashboard.accessDenied.title')}</h2>
                <p>{t('dashboard.accessDenied.page')}</p>
              </div>
            ) : (
              <ProductList />
            )}
          </div>
        )}

        {/* Giderler (Expenses) Tab */}
        {hasMountedExpenses && (
          <div style={{ display: activeTab === 'expenses' ? 'block' : 'none' }}>
            {!permissions?.canAccessExpenses ? (
              <div className="access-denied-card">
                <i className="fas fa-lock"></i>
                <h2>{t('dashboard.accessDenied.title')}</h2>
                <p>{t('dashboard.accessDenied.page')}</p>
              </div>
            ) : (
              <ExpenseList onExpenseChange={handleExpenseChange} />
            )}
          </div>
        )}

        {/* Kullanıcı Yönetimi (Users) Tab */}
        {hasMountedUsers && (
          <div style={{ display: activeTab === 'users' ? 'block' : 'none' }}>
            {!permissions?.canManageUsers ? (
              <div className="access-denied-card">
                <i className="fas fa-lock"></i>
                <h2>{t('dashboard.accessDenied.title')}</h2>
                <p>{t('dashboard.accessDenied.page')}</p>
              </div>
            ) : (
              renderUserManagement()
            )}
          </div>
        )}
      </main>

      <footer className="dashboard-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <i className="fas fa-store"></i>
            <span>{APP_NAME}</span>
          </div>

          <div className="footer-center">
            <span className="footer-dev-label">{t('common.developedBy')}</span>
            <a
              href="https://www.kahramantech.com"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-dev-link"
            >
              <i className="fas fa-code footer-dev-icon"></i>
              <span>KahramanTech</span>
              <i className="fas fa-external-link-alt footer-ext-icon"></i>
            </a>
          </div>

          <div className="footer-right">
            <span className="footer-copyright">© {new Date().getFullYear()} {t('common.allRightsReserved')}</span>
            <span className="footer-sep">·</span>
            <span className="version-badge">v{APP_VERSION}</span>
          </div>
        </div>
      </footer>

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal-content import-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-upload"></i>
                {t('dashboard.backup.modalTitle')}
              </h3>
              <button onClick={closeImportModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="file-upload-area">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  id="backup-file-input"
                  className="file-input"
                />
                <label htmlFor="backup-file-input" className="file-upload-label">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>{t('dashboard.backup.selectFile')}</span>
                </label>

                {importFile && (
                  <div className="selected-file">
                    <i className="fas fa-file-alt"></i>
                    <span>{importFile.name}</span>
                    <small>({(importFile.size / 1024).toFixed(1)} KB)</small>
                  </div>
                )}
              </div>

              <div className="import-warning">
                <i className="fas fa-exclamation-triangle"></i>
                <div>
                  <strong>{t('dashboard.backup.warningTitle')}</strong>
                  <ul>
                    <li>{t('dashboard.backup.warningJson')}</li>
                    <li>{t('dashboard.backup.warningKeepData')}</li>
                    <li>{t('dashboard.backup.warningUpdateIds')}</li>
                    <li>{t('dashboard.backup.warningIrreversible')}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleImport}
                disabled={!importFile || isImporting}
                className="btn-primary import-confirm-btn"
              >
                {isImporting ? (
                  <span>
                    <div className="btn-spinner"></div>
                    {t('dashboard.backup.uploading')}
                  </span>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    {t('dashboard.backup.importButton')}
                  </>
                )}
              </button>
              <button
                onClick={closeImportModal}
                disabled={isImporting}
                className="btn-secondary"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notificationModal.show && (
        <div className="modal-overlay">
          <div className={`modal-content notification-modal ${notificationModal.type}`}>
            <div className="modal-header">
              <h3>
                {notificationModal.type === 'success' && <i className="fas fa-check-circle"></i>}
                {notificationModal.type === 'error' && <i className="fas fa-times-circle"></i>}
                {notificationModal.type === 'warning' && <i className="fas fa-exclamation-triangle"></i>}
                {notificationModal.type === 'info' && <i className="fas fa-info-circle"></i>}
                {notificationModal.title}
              </h3>
              <button onClick={closeNotificationModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p className="notification-message">{notificationModal.message}</p>

              {/* Export detayları */}
              {notificationModal.details && notificationModal.details.customers !== undefined && !notificationModal.details.backupCustomers && (
                <div className="notification-details">
                  <div className="detail-item">
                    <i className="fas fa-users"></i>
                    <span>{t('common.customerCount', { count: notificationModal.details.customers })}</span>
                  </div>
                  <div className="detail-item">
                    <i className="fas fa-shopping-cart"></i>
                    <span>{t('common.orderCount', { count: notificationModal.details.orders })}</span>
                  </div>
                </div>
              )}

              {/* Import detayları */}
              {notificationModal.details && notificationModal.details.backupCustomers !== undefined && (
                <div className="notification-details import-details">
                  <div className="detail-section">
                    <h4>📊 {t('dashboard.backup.backupFile')}</h4>
                    <div className="detail-row">
                      <span>{t('common.customerCount', { count: notificationModal.details.backupCustomers })}</span>
                      <span>{t('common.orderCount', { count: notificationModal.details.backupOrders })}</span>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>✅ {t('dashboard.backup.importResult')}</h4>
                    <div className="detail-grid">
                      <div className="detail-item success">
                        <span className="detail-label">{t('dashboard.backup.newCustomer')}</span>
                        <span className="detail-value">{notificationModal.details.addedCustomers}</span>
                      </div>
                      <div className="detail-item info">
                        <span className="detail-label">{t('dashboard.backup.updatedCustomer')}</span>
                        <span className="detail-value">{notificationModal.details.updatedCustomers}</span>
                      </div>
                      <div className="detail-item success">
                        <span className="detail-label">{t('dashboard.backup.newOrder')}</span>
                        <span className="detail-value">{notificationModal.details.addedOrders}</span>
                      </div>
                      <div className="detail-item info">
                        <span className="detail-label">{t('dashboard.backup.updatedOrder')}</span>
                        <span className="detail-value">{notificationModal.details.updatedOrders}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={closeNotificationModal} className="btn-primary">
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Firma Silme Onay Modal */}
      {deleteCompanyModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-company-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle"></i>
                {t('dashboard.company.deleteTitle')}
              </h3>
              <button onClick={handleCloseDeleteCompanyModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>
                <Trans
                  i18nKey="dashboard.company.deleteConfirm"
                  values={{ name: deleteCompanyModal.name }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <p className="warning-text">
                <i className="fas fa-exclamation-circle"></i>
                {t('common.cannotUndo')}
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleDeleteCompany}
                className="btn-primary delete-confirm-btn"
                disabled={isLoadingCompanies}
              >
                {isLoadingCompanies ? (
                  <>
                    <div className="btn-spinner"></div>
                    {t('common.deleting')}
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash"></i>
                    {t('common.yesDelete')}
                  </>
                )}
              </button>
              <button
                onClick={handleCloseDeleteCompanyModal}
                className="btn-secondary"
                disabled={isLoadingCompanies}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Kullanıcı Ekleme Modal */}
      {showAddUserModal && (
        <div className="modal-overlay">
          <div className="modal-content add-user-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-user-plus"></i>
                {t('users.createForm.title')}
              </h3>
              <button onClick={() => {
                setShowAddUserModal(false);
                setNewUserForm({ email: '', password: '', displayName: '', role: ROLES.SATISCI });
                setUserFormError(null);
              }} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleCreateUser}>
              <div className="modal-body">
                {userFormError && (
                  <div className="form-error">
                    <i className="fas fa-exclamation-circle"></i>
                    {userFormError}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="displayName">
                    <i className="fas fa-user"></i>
                    {t('users.createForm.displayName')}
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    value={newUserForm.displayName}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, displayName: e.target.value }))}
                    placeholder={t('users.createForm.displayNamePlaceholder')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <i className="fas fa-envelope"></i>
                    {t('users.createForm.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder={t('users.createForm.emailPlaceholder')}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    <i className="fas fa-lock"></i>
                    {t('users.createForm.password')}
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder={t('users.createForm.passwordPlaceholder')}
                    minLength={6}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role">
                    <i className="fas fa-user-tag"></i>
                    {t('users.createForm.role')}
                  </label>
                  <select
                    id="role"
                    value={newUserForm.role}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <option value={ROLES.SATISCI}>{getRoleName(ROLES.SATISCI)}</option>
                    <option value={ROLES.MUHASEBECI}>{getRoleName(ROLES.MUHASEBECI)}</option>
                    <option value={ROLES.MONTAJCI}>{getRoleName(ROLES.MONTAJCI)}</option>
                    <option value={ROLES.YAZILIMCI}>{getRoleName(ROLES.YAZILIMCI)}</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="submit" className="btn-primary">
                  <i className="fas fa-user-plus"></i>
                  {t('users.createForm.submit')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUserModal(false);
                    setNewUserForm({ email: '', password: '', displayName: '', role: ROLES.SATISCI });
                    setUserFormError(null);
                  }}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Kullanıcı Silme Onay Modal */}
      {deleteUserModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-user-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle"></i>
                {t('users.deleteModal.title')}
              </h3>
              <button onClick={() => setDeleteUserModal(null)} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>
                <Trans
                  i18nKey="users.deleteModal.confirm"
                  values={{ name: deleteUserModal.displayName || deleteUserModal.email }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <p className="warning-text">
                <i className="fas fa-exclamation-circle"></i>
                {t('users.deleteModal.warning')}
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => handleDeleteUser(deleteUserModal.id)}
                className="btn-primary delete-confirm-btn"
              >
                <i className="fas fa-trash"></i>
                {t('users.deleteModal.confirmBtn')}
              </button>
              <button
                onClick={() => setDeleteUserModal(null)}
                className="btn-secondary"
              >
                {t('users.deleteModal.cancelBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Şifre Değiştirme Onay Modal */}
      {showPasswordConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-user-modal">
            <div className="modal-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fas fa-exclamation-triangle" style={{ color: '#f39c12', fontSize: '1.3rem' }}></i>
                {t('dashboard.password.confirmModalTitle')}
              </h3>
              <button onClick={() => setShowPasswordConfirmModal(false)} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p style={{ fontSize: '1rem', lineHeight: '1.5', marginBottom: '15px' }}>
                <strong>{t('dashboard.password.confirmModalLead')}</strong>
              </p>
              <div className="warning-text" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: 'rgba(243, 156, 18, 0.1)', borderLeft: '4px solid #f39c12', color: 'inherit', padding: '12px' }}>
                <i className="fas fa-info-circle" style={{ color: '#f39c12', fontSize: '1.2rem', marginTop: '2px' }}></i>
                <div>
                  <strong style={{ display: 'block', marginBottom: '4px' }}>{t('dashboard.password.confirmModalInfoTitle')}</strong>
                  <Trans i18nKey="dashboard.password.confirmModalInfo" />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleChangePasswordConfirm}
                className="btn-primary"
                style={{ background: 'linear-gradient(135deg, var(--primary-500) 0%, var(--primary-600) 100%)', borderColor: 'transparent' }}
              >
                <i className="fas fa-check"></i>
                {t('common.yesChange')}
              </button>
              <button
                onClick={() => setShowPasswordConfirmModal(false)}
                className="btn-secondary"
              >
                {t('common.giveUp')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;